import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Ban, CheckCircle2, Clipboard, ExternalLink, Eye, File as FileIcon, FileSignature, FlaskConical,
  IdCard, Image as ImageIcon, Lock, Pencil, Pill, Plus, RefreshCw, Send as SendIcon, Shield, Stethoscope, TestTubes,
  Trash2, Upload as UploadIcon, ChevronUp, ChevronDown, X,
} from "lucide-react";
import {
  Badge, Button, Drawer, Input, Label, Modal, ModalFooter, Select, Skeleton, Textarea,
  useConfirm, useToast,
} from "@/components/ui";
import { PanelErrorRow } from "./PanelErrorRow";
import { usePanelData } from "./usePanelData";
import { api, ApiError } from "@/lib/api";
import { mergeNote, noteToSave } from "@/lib/chart-note";
import { cn } from "@/lib/utils";
import { documentTitleFor, formatDate, formatMoney, formatRelative, formatTime } from "@/lib/format";
import { uploadFile, makeUploadFilename } from "@/lib/upload";
import { useCan, useMe } from "@/lib/me";
import type {
  Chart, ChartAddendum, ClientDocument, CreditEntry, FormField, FormRequest, FormSection, FormTemplate,
  MedicalProfile, MembershipSubscription, PackageBalance, PackageRedemption,
} from "@/lib/types";

/**
 * Med-spa specific right-rail panels for the client detail page:
 * medical profile, clinical charts (SOAP / freeform notes with sign-and-lock),
 * client documents (lab scripts, results, consents…), intake/consent form
 * requests, memberships + account credit, and package visit history. Same
 * self-contained contract as InfoPanels — each panel takes a `contactId` and
 * fetches its own data.
 */

// ─── Shared chrome ────────────────────────────────────────────────────────
export function Panel({
  title, action, children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <div className="flex items-center justify-between border-b border-zinc-800/70 px-4 py-3">
        {/* "can we make titles larger and bold possible blue and carry that
            through out the software" — same heading on every panel, in the
            same blue, so a nurse scanning the chart finds the section she
            wants by its colour. */}
        <h3 className="text-sm font-bold tracking-wide text-sky-300">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function HeaderAction({
  onClick, icon: Icon, label,
}: {
  onClick: () => void;
  icon: ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function PanelSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={cn("h-4", i % 2 === 0 ? "w-full" : "w-2/3")} />
      ))}
    </div>
  );
}

function PanelEmpty({ children = "None yet" }: { children?: ReactNode }) {
  return <p className="px-4 py-3 text-xs text-zinc-500">{children}</p>;
}

function IconAction({
  onClick, icon: Icon, label, disabled, danger,
}: {
  onClick: () => void;
  icon: ComponentType<{ className?: string }>;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 disabled:opacity-40",
        danger ? "hover:bg-rose-500/10 hover:text-rose-300" : "hover:bg-zinc-800 hover:text-zinc-200",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// ═══ Medical ══════════════════════════════════════════════════════════════
const EMPTY_DASH = <span className="text-zinc-600">—</span>;

function MedicalRow({ label, value }: { label: string; value: string }) {
  const trimmed = (value || "").trim();
  return (
    <div className="px-4 py-2">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-200">{trimmed || EMPTY_DASH}</p>
    </div>
  );
}

export function MedicalPanel({ contactId }: { contactId: string }) {
  const { data: profile, error, reload } = usePanelData(
    () => api.get<{ profile: MedicalProfile }>(`/medical/${contactId}`).then((d) => d.profile),
    [contactId]
  );
  const [editing, setEditing] = useState(false);

  const allergies = (profile?.allergies || "").trim();
  const emergency = profile
    ? [(profile.emergency_contact_name || "").trim(), (profile.emergency_contact_phone || "").trim()].filter(Boolean).join(" · ")
    : "";

  return (
    <Panel
      title="Medical"
      action={profile ? <HeaderAction onClick={() => setEditing(true)} icon={Pencil} label="Edit" /> : undefined}
    >
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : profile === null ? (
        <PanelSkeleton rows={3} />
      ) : (
        <div className="divide-y divide-zinc-800/40 py-1">
          <div className="px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Allergies</p>
            {allergies ? (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-300" />
                <p className="whitespace-pre-wrap text-sm font-medium text-rose-200">{allergies}</p>
              </div>
            ) : (
              <p className="mt-0.5 text-sm">{EMPTY_DASH}</p>
            )}
          </div>
          <MedicalRow label="Medications" value={profile.medications} />
          <MedicalRow label="Conditions" value={profile.conditions} />
          <MedicalRow label="Emergency contact" value={emergency} />
          <MedicalRow label="Physician" value={profile.primary_physician} />
          <MedicalRow label="Notes" value={profile.notes} />
        </div>
      )}
      {editing && profile && (
        <MedicalEditDrawer
          contactId={contactId}
          profile={profile}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload(); }}
        />
      )}
    </Panel>
  );
}

function MedicalEditDrawer({
  contactId, profile, onClose, onSaved,
}: {
  contactId: string;
  profile: MedicalProfile;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [allergies, setAllergies] = useState(profile.allergies || "");
  const [medications, setMedications] = useState(profile.medications || "");
  const [conditions, setConditions] = useState(profile.conditions || "");
  const [emergencyName, setEmergencyName] = useState(profile.emergency_contact_name || "");
  const [emergencyPhone, setEmergencyPhone] = useState(profile.emergency_contact_phone || "");
  const [physician, setPhysician] = useState(profile.primary_physician || "");
  const [notes, setNotes] = useState(profile.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put<{ profile: MedicalProfile }>(`/medical/${contactId}`, {
        allergies,
        medications,
        conditions,
        emergency_contact_name: emergencyName,
        emergency_contact_phone: emergencyPhone,
        primary_physician: physician,
        notes,
      });
      toast.success("Medical info saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title="Edit medical info" width="md">
      <div className="flex min-h-full flex-col">
        <div className="flex-1 space-y-3 p-5">
          <div>
            <Label htmlFor="med-allergies">Allergies</Label>
            <Textarea id="med-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="e.g. Penicillin, latex…" className="min-h-16" />
          </div>
          <div>
            <Label htmlFor="med-medications">Medications</Label>
            <Textarea id="med-medications" value={medications} onChange={(e) => setMedications(e.target.value)} placeholder="Current medications" className="min-h-16" />
          </div>
          <div>
            <Label htmlFor="med-conditions">Conditions</Label>
            <Textarea id="med-conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Medical conditions" className="min-h-16" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="med-emergency-name">Emergency contact</Label>
              <Input id="med-emergency-name" value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} placeholder="Name" />
            </div>
            <div>
              <Label htmlFor="med-emergency-phone">Emergency phone</Label>
              <Input id="med-emergency-phone" value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} placeholder="Phone" />
            </div>
          </div>
          <div>
            <Label htmlFor="med-physician">Primary physician</Label>
            <Input id="med-physician" value={physician} onChange={(e) => setPhysician(e.target.value)} placeholder="Dr. …" />
          </div>
          <div>
            <Label htmlFor="med-notes">Notes</Label>
            <Textarea id="med-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else the team should know" className="min-h-16" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>Save</Button>
        </div>
      </div>
    </Drawer>
  );
}

// ═══ Charts & notes ═══════════════════════════════════════════════════════
/**
 * "is there any way to take the layout of overview visit notes and have it
 *  show under chart notes where we can scroll and read them - it's easier for
 *  nurses to scroll and skim. If the name and title (of who wrote it) could
 *  also sit at the very top of the note that would be huge, and if we can get
 *  an arrow for scrolling instead of a bar or both but the bar needs to be
 *  bigger, and the names on notes (who wrote it) need color."
 *
 * So the notes are READ here rather than opened one at a time: who wrote it
 * first, in their own colour, then the note itself. The list scrolls on a fat
 * bar with arrows above it, and any note opens full width for a longer read.
 */
const AUTHOR_COLOURS: Record<string, string> = {
  lavender: "text-violet-300", teal: "text-teal-300", amber: "text-amber-300",
  rose: "text-rose-300", sky: "text-sky-300", lime: "text-lime-300",
  violet: "text-violet-300", orange: "text-orange-300", emerald: "text-emerald-300",
  pink: "text-pink-300", cyan: "text-cyan-300", indigo: "text-indigo-300",
};

export function ChartsPanel({ contactId }: { contactId: string }) {
  const { data: charts, error, reload } = usePanelData(
    () => api.get<{ charts: Chart[] }>("/charts", { contact_id: contactId }).then((d) => d.charts),
    [contactId]
  );
  const [open, setOpen] = useState<Chart | "new" | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  // Newest at the top, on the real time rather than letter by letter: a date
  // compared as text sorts "Tue …" above "Sat …", which is how her notes came
  // out in weekday order. The server sends ISO now; this holds either way.
  const sorted = charts
    ? [...charts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    : null;

  const nudge = (dir: 1 | -1) =>
    scroller.current?.scrollBy({ top: dir * 320, behavior: "smooth" });

  return (
    <Panel
      title="Chart notes"
      action={
        <span className="flex items-center gap-1">
          {(sorted?.length ?? 0) > 1 && (
            <>
              <button
                type="button"
                onClick={() => nudge(-1)}
                aria-label="Scroll up"
                className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => nudge(1)}
                aria-label="Scroll down"
                className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </>
          )}
          <HeaderAction onClick={() => setOpen("new")} icon={Plus} label="New" />
        </span>
      }
    >
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : sorted === null ? (
        <PanelSkeleton rows={3} />
      ) : sorted.length === 0 ? (
        <PanelEmpty />
      ) : (
        <div
          ref={scroller}
          data-chart-scroller
          className="chart-scroll max-h-[34rem] divide-y divide-zinc-800/40 overflow-y-auto"
        >
          {sorted.map((c) => {
            const note = mergeNote(c);
            const colour = AUTHOR_COLOURS[c.author_color ?? ""] ?? "text-sky-300";
            return (
              <article key={c.id} data-chart-note className="px-4 py-3">
                {/* Who wrote it, at the very top, in their own colour. */}
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className={cn("text-sm font-semibold", colour)}>
                    {c.author_name || c.signed_by || "Unsigned"}
                  </span>
                  {c.author_credential && (
                    <span className={cn("text-xs font-medium", colour)}>{c.author_credential}</span>
                  )}
                  <span className="text-xs text-zinc-500">{formatDate(c.created_at)}</span>
                  <Badge color={c.status === "signed" ? "emerald" : "amber"} size="xs">
                    {c.status === "signed" ? "Signed" : "Draft"}
                  </Badge>
                  {c.review_status === "needs_review" && (
                    <Badge color="sky" size="xs">With the medical director</Badge>
                  )}
                  {/* "can this just say reviewed by Dr Peggy Bastien Medical
                      Director" - a countersigned chart says so, and by whom,
                      where it used to look identical to one nobody had read. */}
                  {c.review_status === "reviewed" && c.reviewed_by && (
                    <Badge color="emerald" size="xs">Reviewed by {c.reviewed_by}</Badge>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpen(c)}
                    className="ml-auto shrink-0 text-[11px] font-medium text-sky-300 hover:text-sky-200"
                  >
                    {c.status === "signed" ? "Open" : "Edit"}
                  </button>
                </div>
                <p className="mt-0.5 text-xs font-medium text-zinc-300">{c.title || "Untitled note"}</p>
                {note ? (
                  <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{note}</p>
                ) : (
                  <p className="mt-1.5 text-sm text-zinc-600">Nothing written in it yet.</p>
                )}
                {c.addenda_count > 0 && (
                  <p className="mt-1.5 text-[11px] text-zinc-500">
                    +{c.addenda_count} {c.addenda_count === 1 ? "addendum" : "addenda"}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      )}
      {open !== null && (
        <ChartDrawer
          contactId={contactId}
          target={open}
          onClose={() => setOpen(null)}
          onChanged={reload}
        />
      )}
    </Panel>
  );
}

function ChartDrawer({
  contactId, target, onClose, onChanged,
}: {
  contactId: string;
  target: Chart | "new";
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = target === "new";

  const [chart, setChart] = useState<Chart | null>(isNew ? null : target);
  const [addenda, setAddenda] = useState<ChartAddendum[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [chartType, setChartType] = useState<"soap" | "note" | "nursing" | "medical_director">(isNew ? "soap" : target.chart_type);
  // Pre-designed notes the team alters per patient (Settings-seeded).
  const [templates, setTemplates] = useState<ChartTemplate[]>([]);
  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    api.get<{ templates: ChartTemplate[] }>("/charts/templates")
      .then((d) => { if (!cancelled) setTemplates(d.templates); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isNew]);
  const [managingTemplates, setManagingTemplates] = useState(false);
  const reloadTemplates = () =>
    api.get<{ templates: ChartTemplate[] }>("/charts/templates")
      .then((d) => setTemplates(d.templates)).catch(() => {});
  const applyTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setChartType(t.chart_type);
    if (t.title) setTitle(t.title);
    // Her SOAP templates are stored in four columns; the provider gets them as
    // one note with the headings in it. "it's all one".
    setBody(mergeNote(t));
  };
  const [title, setTitle] = useState(isNew ? "" : target.title);
  const [body, setBody] = useState(isNew ? "" : mergeNote(target));
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  // "is it possible to allow provider to check off a box to send the chart
  // (not the intake forms) for medical director review … if not it never
  // goes to the list for medical director review". Off by default: "Not all
  // charts are reviewed by MD just certain ones".
  const [mdReview, setMdReview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addendumBody, setAddendumBody] = useState("");
  const [addingAddendum, setAddingAddendum] = useState(false);
  /* The medical director's countersignature. */
  const me = useMe();
  const [reviewNote, setReviewNote] = useState("");
  const [reviewing, setReviewing] = useState(false);

  // Existing charts get a detail fetch so the drawer shows fresh field
  // values + the addenda list (the list endpoint only carries a count).
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api.get<{ chart: Chart; addenda: ChartAddendum[] }>(`/charts/${target.id}`)
      .then((d) => {
        if (cancelled) return;
        setChart(d.chart);
        setAddenda(d.addenda);
        setChartType(d.chart.chart_type);
        setTitle(d.chart.title);
        setBody(mergeNote(d.chart));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isNew, target]);

  const signed = chart?.status === "signed";

  const saveDraft = async () => {
    if (!title.trim()) {
      toast.error("Give the chart a title");
      return;
    }
    // One note. The old per-section columns are cleared as it saves, so an
    // older note that is edited is not left stored in two places.
    const payload = { chart_type: chartType, title: title.trim(), ...noteToSave(body) };
    setSaving(true);
    try {
      if (chart) {
        const r = await api.patch<{ chart: Chart }>(`/charts/${chart.id}`, payload);
        setChart(r.chart);
      } else {
        const r = await api.post<{ chart: Chart }>("/charts", { contact_id: contactId, ...payload });
        setChart(r.chart);
      }
      toast.success("Draft saved");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sign = async () => {
    if (!chart) return;
    const ok = await confirm({
      title: "Sign this chart?",
      description: mdReview
        ? "Signed charts lock forever - corrections become addenda. This one also goes to the medical director."
        : "Signed charts lock forever - corrections become addenda.",
      confirmLabel: "Sign & lock",
    });
    if (!ok) return;
    setSigning(true);
    try {
      const r = await api.post<{ chart: Chart }>(`/charts/${chart.id}/sign`, { md_review: mdReview });
      setChart(r.chart);
      toast.success(mdReview ? "Signed and sent to the medical director" : "Chart signed");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to sign");
    } finally {
      setSigning(false);
    }
  };

  const remove = async () => {
    if (!chart) return;
    const ok = await confirm({
      title: "Delete this draft?",
      description: "The draft will be permanently removed.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/charts/${chart.id}`);
      toast.success("Draft deleted");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
      setDeleting(false);
    }
  };

  /**
   * "the medical director will come in here and then she's going to review
   *  these charts … she can log in, review the chart and say chart reviewed."
   *
   * Countersigning, not editing: the chart is untouched and corrections still
   * go through an addendum.
   */
  const markReviewed = async () => {
    if (!chart) return;
    setReviewing(true);
    try {
      const r = await api.post<{ chart: Chart }>(`/charts/${chart.id}/review`,
        reviewNote.trim() ? { note: reviewNote.trim() } : {});
      setChart(r.chart);
      setReviewNote("");
      toast.success("Chart reviewed", "It is countersigned in your name.");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't record the review");
    } finally {
      setReviewing(false);
    }
  };

  const addAddendum = async () => {
    const text = addendumBody.trim();
    if (!text || !chart) return;
    setAddingAddendum(true);
    try {
      const r = await api.post<{ addendum: ChartAddendum }>(`/charts/${chart.id}/addenda`, { body: text });
      setAddenda((prev) => [...prev, r.addendum]);
      setAddendumBody("");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to add addendum");
    } finally {
      setAddingAddendum(false);
    }
  };

  // One note, however it was stored. A note signed before this change still
  // has its text in the four columns; it reads as one note and is never
  // rewritten.
  const signedNote = mergeNote(chart);

  return (
    // "if we can open the Charts for larger read would be nice" — the widest
    // the drawer goes, for reading a long note rather than skimming it.
    <Drawer open onClose={onClose} title={isNew ? "New chart" : signed ? chart?.title || "Chart" : "Edit chart"} width="xl">
      <div className="flex min-h-full flex-col">
        {loading ? (
          <div className="space-y-3 p-5">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-2/3" />
          </div>
        ) : loadError ? (
          <PanelErrorRow message={loadError} />
        ) : signed && chart ? (
          <div className="flex-1 space-y-4 p-5">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
              <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-300" />
              Signed by {chart.signed_by || "unknown"} · {formatDate(chart.signed_at)}
            </div>
            {chart.review_status === "needs_review" && (
              <div data-md-status className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                <div className="flex items-center gap-2">
                  <Stethoscope className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1">
                    With the medical director{chart.review_requested_by ? ` — sent by ${chart.review_requested_by}` : ""}
                  </span>
                </div>
                {/* The countersignature itself. The rule was written months
                    ago and the queue was built, but there was never a button
                    to press: the medical director signed in, read the chart,
                    found nothing to press and wrote a note of her own saying
                    she had reviewed it. One press now, from the chart she is
                    already looking at. */}
                {me?.can_review_charts && (
                  <div className="mt-2 space-y-2">
                    <Input
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="Anything to add (optional)"
                      className="h-8 text-xs"
                    />
                    <Button size="sm" loading={reviewing} onClick={() => void markReviewed()}
                      leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}>
                      Chart reviewed
                    </Button>
                  </div>
                )}
              </div>
            )}
            {chart.review_status === "reviewed" && chart.reviewed_by && (
              <div data-md-status className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span>Reviewed by {chart.reviewed_by} · {formatDate(chart.reviewed_at)}</span>
                </div>
                {chart.review_note && (
                  <p className="mt-1 whitespace-pre-wrap pl-5.5 text-sky-100/90">{chart.review_note}</p>
                )}
              </div>
            )}
            {signedNote === "" ? (
              <p className="text-sm text-zinc-500">This chart has no content.</p>
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-200">{signedNote}</p>
            )}
            <div className="border-t border-zinc-800/60 pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                Addenda{addenda.length > 0 ? ` (${addenda.length})` : ""}
              </p>
              {addenda.length === 0 ? (
                <p className="mt-1.5 text-xs text-zinc-500">No addenda yet.</p>
              ) : (
                <ul className="mt-1.5 space-y-2.5">
                  {addenda.map((a) => (
                    <li key={a.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
                      <p className="whitespace-pre-wrap text-sm text-zinc-200">{a.body}</p>
                      <p className="mt-1 text-[10px] text-zinc-500">
                        {a.author_name ? `${a.author_name} · ` : ""}{formatDate(a.created_at)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 space-y-2">
                <Textarea
                  value={addendumBody}
                  onChange={(e) => setAddendumBody(e.target.value)}
                  placeholder="Add a correction or follow-up…"
                  className="min-h-16 text-xs"
                />
                <Button size="sm" variant="outline" onClick={addAddendum} loading={addingAddendum} disabled={!addendumBody.trim()}>
                  Add addendum
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 p-5">
              <div className="inline-flex rounded-lg border border-zinc-800 bg-zinc-900/60 p-0.5">
                {/* Her structure: providers chart Assessment + Plan; the
                    RN/LPN writes a Nursing note. Freeform stays for
                    anything else. Signature is stamped automatically with
                    the writer's name + credential on sign. */}
                {/* "can I have Medical Director up with Provider Nursing" —
                    she reads somebody else's chart and writes her own note
                    saying she reviewed it, so it is a kind of note, not a
                    tick. */}
                {([["soap", "Provider note"], ["nursing", "Nursing note"],
                   ["medical_director", "Medical Director"], ["note", "Freeform"]] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setChartType(value)}
                    className={cn(
                      "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                      chartType === value ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {isNew && templates.length > 0 && (
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <Label htmlFor="chart-template">Start from a template</Label>
                    {/* "there are changes we need to make to our pre completed
                        notes can we be able to edit drafts and/or create new
                        notes" — hers to change, from where she uses them. */}
                    <button type="button" onClick={() => setManagingTemplates(true)}
                      className="text-[11px] font-medium text-sky-300 hover:text-sky-200">
                      Edit these notes
                    </button>
                  </div>
                  <Select id="chart-template" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
                    <option value="">Blank note</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="chart-title">Title</Label>
                <Input id="chart-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Botox follow-up" />
              </div>
              {/* "it has to be one note" — one box for every kind of note, with
                  the headings part of the text rather than the furniture. */}
              <div>
                <Label htmlFor="chart-body">
                  {chartType === "nursing" ? "Nursing note"
                    : chartType === "medical_director" ? "Medical director's note"
                      : "Note"}
                </Label>
                <Textarea
                  id="chart-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="min-h-[28rem]"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-zinc-800 px-5 py-3">
              <div>
                {chart && (
                  <Button variant="ghost" size="sm" onClick={remove} loading={deleting} leftIcon={<Trash2 className="h-3.5 w-3.5" />} className="text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">
                    Delete draft
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {chart && (
                  <label className="mr-1 flex cursor-pointer items-center gap-2 text-xs text-zinc-300" data-md-review>
                    <input
                      type="checkbox"
                      checked={mdReview}
                      onChange={(e) => setMdReview(e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                    />
                    Send to the medical director
                  </label>
                )}
                <Button variant="ghost" onClick={onClose}>Close</Button>
                <Button variant="secondary" onClick={saveDraft} loading={saving}>Save draft</Button>
                {chart && (
                  <Button onClick={sign} loading={signing} leftIcon={<Lock className="h-3.5 w-3.5" />}>
                    Sign & lock
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {managingTemplates && (
        <ChartTemplateManager
          templates={templates}
          onClose={() => setManagingTemplates(false)}
          onChanged={() => void reloadTemplates()}
        />
      )}
    </Drawer>
  );
}

/**
 * The pre-written notes, edited where they are used.
 *
 *   "as we grow in here there are changes we need to make to our pre
 *    completed notes can we be able to edit drafts and/or create new notes"
 *
 * Only people who sign notes can open the drawer this sits in, which is the
 * three she named. An old note is archived rather than deleted, so a chart
 * already written from it keeps its history.
 */
function ChartTemplateManager({ templates, onClose, onChanged }: {
  templates: ChartTemplate[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<ChartTemplate | "new" | null>(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const open = (t: ChartTemplate | "new") => {
    setEditing(t);
    setName(t === "new" ? "" : t.name);
    setText(t === "new" ? "" : mergeNote(t));
  };

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      // Written as one note, the way she reads them back.
      const payload = { name: name.trim(), chart_type: "note", title: name.trim(), body: text };
      if (editing === "new") await api.post("/charts/templates", payload);
      else if (editing) await api.patch(`/charts/templates/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that note");
    } finally { setBusy(false); }
  };

  const archive = async (t: ChartTemplate) => {
    const ok = await confirm({
      title: `Put "${t.name}" away?`,
      description: "It stops being offered on new notes. Notes already written from it are untouched.",
      confirmLabel: "Put it away",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/charts/templates/${t.id}`);
      toast.success("Put away");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "That didn't work");
    }
  };

  return (
    <Modal open onClose={onClose} title="Pre-written notes" size="md">
      {editing === null ? (
        <div className="space-y-2">
          <ul className="divide-y divide-zinc-800/40 rounded-lg border border-zinc-800">
            {templates.length === 0 && <li className="px-3 py-3 text-sm text-zinc-500">Nothing written yet.</li>}
            {templates.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="min-w-0 truncate text-sm text-zinc-200">{t.name}</span>
                <span className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" onClick={() => open(t)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-rose-300 hover:text-rose-200"
                    onClick={() => void archive(t)}>Put away</Button>
                </span>
              </li>
            ))}
          </ul>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button onClick={() => open("new")}>Write a new one</Button>
          </ModalFooter>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label required>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. NAD infusion" />
            <p className="mt-1 text-[11px] text-zinc-500">What it is called in the list.</p>
          </div>
          <div>
            <Label>The note</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-48"
              placeholder="What this note says before anyone changes it for the client in front of them." />
          </div>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Back</Button>
            <Button loading={busy} disabled={!name.trim()} onClick={() => void save()}>Save</Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}


interface ChartTemplate {
  id: string; name: string; chart_type: "soap" | "note" | "nursing" | "medical_director";
  title: string; subjective: string; objective: string; assessment: string; plan: string; body: string;
}

// ═══ Wallet ═══════════════════════════════════════════════════════════════
/**
 * Her word for it: "Wallet - this can hold cc cards account credits products
 * purchased".
 *
 * Credit and purchases are real today. Saved cards are deliberately an honest
 * placeholder rather than a dead button: storing a card means charging it
 * later without anyone at the counter, which only works once the practice's
 * card processor is connected.
 */
interface WalletGiftCard {
  id: string; code: string; initial_cents: number | string; balance_cents: number | string;
  active: boolean; created_at: string; recipient_name: string | null;
  recipient_client_name: string | null; purchaser_name: string | null; theirs: boolean | null;
}

/**
 * The wallet, in her order:
 *
 *   "So blue titles are: Card on File / Vouchers / Account Credits / Gift Cards
 *    / Payments. In that order 😊"
 *
 * Card on file and Payments are their own panels on the tab; these three are
 * the money that already belongs to the client. Each is its own box with its
 * own heading, because she reads down them.
 */
/**
 * What they still have the right to use.
 *
 * "Vouchers - only show vouchers they have to use not already used, that would
 *  be in the pkg/memberships area." So nothing spent, nothing handed over and
 *  nothing posted: just what is still owed to them, and what it is for.
 */

/**
 * Give somebody a voucher without a sale.
 *
 * It makes the same voucher a package sale makes, so it shows in the wallet,
 * spends at the till and counts in her reports like any other - the only
 * difference is there is no invoice behind it, and the reason is written on
 * their file.
 */
function GrantVoucherModal({
  contactId, onClose, onDone,
}: { contactId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [packages, setPackages] = useState<{ id: string; name: string }[] | null>(null);
  const [services, setServices] = useState<{ id: string; name: string }[]>([]);
  /* "are we able to add our single services not just packages" — her
     Boulevard memberships do not match a package she sells (Fran is owed
     eight butterfly pushes; the nearest package grants six). */
  const [kind, setKind] = useState<"package" | "service">("package");
  const [packageId, setPackageId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [sessions, setSessions] = useState(1);
  const [qty, setQty] = useState(1);
  const [fulfilment, setFulfilment] = useState<"" | "in_lounge" | "take_home" | "mail_out">("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ packages: { id: string; name: string; archived?: boolean }[] }>("/pos/packages")
      .then((d) => setPackages((d.packages ?? []).filter((p) => !p.archived)))
      .catch(() => setPackages([]));
    api.get<{ services: { id: string; name: string; archived?: boolean }[] }>("/services")
      .then((d) => setServices((d.services ?? []).filter((x) => !x.archived)))
      .catch(() => setServices([]));
  }, []);

  const save = async () => {
    if (kind === "package" && !packageId) { toast.error("Pick a package first"); return; }
    if (kind === "service" && !serviceId) { toast.error("Pick a treatment first"); return; }
    setSaving(true);
    try {
      const r = await api.post<{ granted?: number; package?: string }>("/pos/package-balances/grant", {
        contact_id: contactId,
        ...(kind === "package" ? { package_id: packageId, qty } : { service_id: serviceId, sessions }),
        ...(fulfilment ? { fulfilment } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      const what = kind === "package"
        ? (r.package ?? "That package")
        : `${services.find((x) => x.id === serviceId)?.name ?? "That treatment"} ×${sessions}`;
      toast.success("On their file", `${what} is in their wallet now.`);
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that voucher");
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add a voucher" size="md">
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          Puts the package on their file without ringing up a sale. Nothing is charged and no
          receipt goes out.
        </p>
        <div>
          <Label htmlFor="grant-kind">A package or a single treatment</Label>
          <Select id="grant-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="package">A package off the menu</option>
            <option value="service">One treatment, however many you say</option>
          </Select>
        </div>
        {kind === "package" ? (
          <div>
            <Label htmlFor="grant-pkg">Which package</Label>
            {packages === null ? (
              <Skeleton className="h-9" />
            ) : (
              <Select id="grant-pkg" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
                <option value="">Choose…</option>
                {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            )}
          </div>
        ) : (
          <div>
            <Label htmlFor="grant-svc">Which treatment</Label>
            <Select id="grant-svc" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
              <option value="">Choose…</option>
              {services.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="grant-qty">How many</Label>
            {kind === "package" ? (
              <Input id="grant-qty" type="number" min={1} max={20} value={String(qty)}
                onChange={(e) => setQty(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))} />
            ) : (
              <Input id="grant-qty" type="number" min={1} max={100} value={String(sessions)}
                onChange={(e) => setSessions(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))} />
            )}
          </div>
          <div>
            <Label htmlFor="grant-ful">How they get it</Label>
            <Select id="grant-ful" value={fulfilment}
              onChange={(e) => setFulfilment(e.target.value as typeof fulfilment)}>
              <option value="">However the package is set</option>
              <option value="in_lounge">In the lounge</option>
              <option value="take_home">Taken home now</option>
              <option value="mail_out">Posted out</option>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="grant-why">Why (optional)</Label>
          <Input id="grant-why" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. making it right, bought before we switched over" />
          <p className="mt-1 text-[11px] text-zinc-500">Written on their file so it is answerable later.</p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {/* A single treatment leaves packageId empty, so keying the button off it
            alone left "Add it" dead for every one-treatment voucher: "I'm not able
            to add those vouchers it won't let me hit add it". Whichever kind is
            showing is the one that has to be filled in. */}
        <Button onClick={() => void save()} loading={saving}
          disabled={kind === "package" ? !packageId : !serviceId}>Add it</Button>
      </ModalFooter>
    </Modal>
  );
}

export function VouchersPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, error, reload } = usePanelData(
    () => api.get<{ balances: PackageBalance[] }>("/pos/package-balances", { contact_id: contactId })
      .then((d) => (d.balances ?? []).filter((b) => (Number(b.sessions_remaining) || 0) > 0)),
    [contactId],
  );
  // "when they are in wallet we need to be able to click ... to open and see
  //  where they came from and date and void if needed"
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // "when I void vouchers I need to put a reason in I just voided one with
  //  Maureen no where to see why I voided one"
  const [voiding, setVoiding] = useState<PackageBalance | null>(null);
  const [granting, setGranting] = useState(false);
  // Giving a voucher away is not a sale, so it is not the till's to do.
  const canGrant = useCan("settings");
  const [sharing, setSharing] = useState<PackageBalance | null>(null);

  const unshare = async (b: PackageBalance, contactId: string) => {
    try {
      await api.delete(`/pos/package-balances/${b.id}/share/${contactId}`);
      toast.success("Taken off the voucher");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove them");
    }
  };
  /* Takes the package off the wallet entirely. Vouchers already used stay,
     because a visit that happened is not undone by tidying a wallet. */
  const removeWhole = async (b: PackageBalance) => {
    const ok = await confirm({
      title: `Remove ${b.package_name || "this package"}?`,
      description: "Every unused voucher in it comes off their wallet. Anything already used stays on their history.",
      confirmLabel: "Remove it",
      danger: true,
    });
    if (!ok) return;
    setBusyId(b.id);
    try {
      const r = await api.delete<{ removed: number; kept_used: number }>(`/pos/package-balances/${b.id}`);
      toast.success(
        `${r.removed} voucher${r.removed === 1 ? "" : "s"} removed`,
        r.kept_used > 0 ? `${r.kept_used} already used, left on their history.` : undefined,
      );
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that package");
    } finally {
      setBusyId(null);
    }
  };

  const doVoid = async (b: PackageBalance, reason: string) => {
    const left = Number(b.sessions_remaining) || 0;
    setBusyId(b.id);
    try {
      await api.post(`/pos/package-balances/${b.id}/void-one`, { reason });
      toast.success("Voucher voided", `${left - 1} of ${b.sessions_total} left. The reason is on their file.`);
      setVoiding(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to void that voucher");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      title="Vouchers"
      /* "can we add a tab to add vouchers only permission is given to myself
         and owners" - making it right after a mistake, honouring something
         bought elsewhere, a goodwill drip. Owners only; the server refuses it
         for anybody else either way. */
      action={canGrant ? (
        <HeaderAction onClick={() => setGranting(true)} icon={Plus} label="Add a voucher" />
      ) : undefined}
    >
      {granting && (
        <GrantVoucherModal
          contactId={contactId}
          onClose={() => setGranting(false)}
          onDone={() => { setGranting(false); reload(); }}
        />
      )}
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : !data ? (
        <PanelSkeleton rows={2} />
      ) : data.length === 0 ? (
        <PanelEmpty>Nothing left to use.</PanelEmpty>
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {data.map((b) => {
            const left = Number(b.sessions_remaining) || 0;
            const total = Number(b.sessions_total) || 0;
            const open = openId === b.id;
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : b.id)}
                  aria-expanded={open}
                  className="flex w-full flex-wrap items-baseline gap-x-2 px-4 py-2.5 text-left hover:bg-zinc-900/40"
                >
                  <ChevronDown className={cn("h-3 w-3 shrink-0 text-zinc-600 transition-transform", open && "rotate-180")} />
                  <span className="min-w-0 truncate text-sm text-zinc-200">
                    {b.package_name || "Package"}
                    {voucherFor(b) && <span className="text-zinc-500"> · {voucherFor(b)}</span>}
                  </span>
                  {b.fulfilment === "mail_out" && <Badge color="amber" size="xs">To post</Badge>}
                  <span className="ml-auto shrink-0 text-sm font-semibold tabular-nums text-emerald-300">
                    {left} <span className="text-xs font-normal text-zinc-500">of {total} left</span>
                  </span>
                </button>
                {open && (
                  <div className="border-t border-zinc-800/40 bg-zinc-900/30 px-4 py-2.5">
                    <dl className="space-y-1 text-xs">
                      <div className="flex justify-between gap-3">
                        <dt className="text-zinc-500">Bought</dt>
                        <dd className="text-zinc-300">{formatDate(b.purchased_at)}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-zinc-500">From</dt>
                        <dd className="min-w-0 truncate text-zinc-300">
                          {b.invoice_number
                            ? `${b.invoice_number}${b.invoice_total_cents != null ? ` · ${formatMoney(Number(b.invoice_total_cents))}` : ""}`
                            : "No ticket on file"}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-zinc-500">Used so far</dt>
                        <dd className="text-zinc-300">{Number(b.sessions_used) || 0} of {total}</dd>
                      </div>
                      {b.expires_at && (
                        <div className="flex justify-between gap-3">
                          <dt className="text-zinc-500">Expires</dt>
                          <dd className="text-zinc-300">{formatDate(b.expires_at)}</dd>
                        </div>
                      )}
                    </dl>
                    {/* "they can share with other clients ... it will show who
                        they are sharing with ... only allow up to 4 ppl" */}
                    <div className="mt-2 border-t border-zinc-800/60 pt-2">
                      <p className="text-[11px] uppercase tracking-wide text-zinc-500">Shared with</p>
                      {(b.shared_with ?? []).length === 0 ? (
                        <p className="mt-0.5 text-xs text-zinc-500">
                          Nobody yet{b.owned === false && b.owner_name ? ` - this one is ${b.owner_name}'s` : ""}.
                        </p>
                      ) : (
                        <ul className="mt-1 space-y-1">
                          {(b.shared_with ?? []).map((p) => (
                            <li key={p.contact_id} className="flex items-center justify-between gap-2 text-xs">
                              <span className="min-w-0 truncate text-zinc-300">{p.name || "A client"}</span>
                              <button
                                type="button"
                                onClick={() => void unshare(b, p.contact_id)}
                                className="shrink-0 text-[11px] text-zinc-500 hover:text-rose-300"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {b.owned !== false && (b.shared_with ?? []).length < 3 && (
                        <button
                          type="button"
                          onClick={() => setSharing(b)}
                          className="mt-1.5 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
                        >
                          + Share with a client
                        </button>
                      )}
                      {b.owned !== false && (b.shared_with ?? []).length >= 3 && (
                        <p className="mt-1 text-[11px] text-zinc-600">Four people is the limit.</p>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-end gap-3">
                      {/* "if we add packages and there are to many can we be
                          able to delete the complete package not just one
                          voucher" — the whole thing, in one go. */}
                      {canGrant && (
                        <button
                          type="button"
                          onClick={() => void removeWhole(b)}
                          disabled={busyId === b.id}
                          className="text-[11px] font-medium text-zinc-500 hover:text-rose-300 disabled:opacity-40"
                        >
                          Remove the whole package
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setVoiding(b)}
                        disabled={busyId === b.id}
                        className="text-[11px] font-medium text-rose-300 hover:text-rose-200 disabled:opacity-40"
                      >
                        Void one
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {sharing && (
        <ShareVoucherModal
          balance={sharing}
          onClose={() => setSharing(null)}
          onShared={() => { setSharing(null); reload(); }}
        />
      )}
      {voiding && (
        <VoidVoucherModal
          balance={voiding}
          busy={busyId === voiding.id}
          onClose={() => setVoiding(null)}
          onConfirm={(reason) => void doVoid(voiding, reason)}
        />
      )}
    </Panel>
  );
}

/**
 * Share a voucher with another client.
 *
 * Searches her client list rather than making her paste an id, and the
 * server has the final say on the limit - this only hides the button early.
 */
function ShareVoucherModal({
  balance, onClose, onShared,
}: {
  balance: PackageBalance;
  onClose: () => void;
  onShared: () => void;
}) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    let off = false;
    const t = setTimeout(() => {
      api.get<{ contacts: { id: string; first_name: string | null; last_name: string | null }[] }>(
        "/contacts", { q: term, limit: 8 })
        .then((d) => {
          if (off) return;
          setResults((d.contacts ?? [])
            .filter((c) => c.id !== balance.contact_id)
            .map((c) => ({ id: c.id, name: `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "A client" })));
        })
        .catch(() => { if (!off) setResults([]); });
    }, 250);
    return () => { off = true; clearTimeout(t); };
  }, [q, balance.contact_id]);

  const share = async (contactId: string) => {
    setBusy(true);
    try {
      await api.post(`/pos/package-balances/${balance.id}/share`, { contact_id: contactId });
      toast.success("Shared", "It now shows in both charts, and using it drops the count in both.");
      onShared();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't share that voucher");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Share ${balance.package_name || "this voucher"}`} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-zinc-400">
          They will see it in their own wallet and can use it. There is one voucher between you,
          so whoever uses it, the count drops for everybody on it. Four people at most.
        </p>
        <div>
          <Label htmlFor="share-who">Who with?</Label>
          <Input
            id="share-who"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Start typing a client's name"
            autoFocus
          />
        </div>
        {q.trim().length >= 2 && (
          results.length === 0 ? (
            <p className="text-xs text-zinc-500">Nobody matches &ldquo;{q.trim()}&rdquo;.</p>
          ) : (
            <ul className="max-h-56 divide-y divide-zinc-800/40 overflow-y-auto rounded-md border border-zinc-800">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void share(r.id)}
                    className="w-full px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-900/60 disabled:opacity-50"
                  >
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

/** Writing one voucher off, with a reason that stays on the file. */
function VoidVoucherModal({
  balance, busy, onClose, onConfirm,
}: {
  balance: PackageBalance;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const left = Number(balance.sessions_remaining) || 0;
  const total = Number(balance.sessions_total) || 0;
  const COMMON = ["Used elsewhere", "Expired", "Dropped or spoiled", "Given as a courtesy", "Booked in error"];

  return (
    <Modal open onClose={onClose} title={`Void one of ${balance.package_name || "this package"}`} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-zinc-400">
          This writes off one voucher, leaving {Math.max(0, left - 1)} of {total}. It stays on their file with the date and the reason.
        </p>
        <div>
          <Label htmlFor="void-reason">Why?</Label>
          <Input
            id="void-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. used at another visit"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && reason.trim()) onConfirm(reason.trim()); }}
          />
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {COMMON.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          loading={busy}
          disabled={!reason.trim()}
          className="bg-rose-600 hover:bg-rose-500"
          onClick={() => onConfirm(reason.trim())}
        >
          Void one
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function AccountCreditPanel({ contactId }: { contactId: string }) {
  const { data, error, reload } = usePanelData(
    () => api.get<{ balance_cents: number; entries: CreditEntry[] }>("/pos/credit", { contact_id: contactId }),
    [contactId],
  );
  /**
   * "the account credits I'm unable to add a credit to Saleem"
   *
   * She was right: adding one lived on the Membership tab, and this is the
   * panel she goes to - her own Wallet, in her own order, called Account
   * Credits. The button belongs where she is looking.
   */
  const [adding, setAdding] = useState(false);

  return (
    <Panel
      title="Account Credits"
      action={<HeaderAction onClick={() => setAdding(true)} icon={Plus} label="Add credit" />}
    >
      {adding && (
        <AddCreditModal
          contactId={contactId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); reload(); }}
        />
      )}
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : !data ? (
        <PanelSkeleton rows={2} />
      ) : (
        <div className="divide-y divide-zinc-800/60">
          <div className="flex items-baseline justify-between px-4 py-3">
            <span className="text-xs uppercase tracking-wide text-zinc-500">On their account</span>
            <span className="text-lg font-semibold tabular-nums text-zinc-100">
              {formatMoney(Number(data.balance_cents ?? 0))}
            </span>
          </div>
          {(data.entries ?? []).length > 0 && (
            <div className="px-4 py-3">
              <p className="mb-2 text-xs uppercase tracking-wide text-zinc-500">Where it came from</p>
              <ul className="space-y-1.5">
                {data.entries.slice(0, 8).map((e) => (
                  <li key={e.id} className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="min-w-0 truncate text-zinc-300">{e.reason || "Adjustment"}</span>
                    <span className="shrink-0 tabular-nums text-zinc-400">
                      {formatMoney(Number(e.amount_cents ?? 0))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

interface WalletGiftCard {
  id: string; code: string; initial_cents: number | string; balance_cents: number | string;
  active: boolean; created_at: string; recipient_name: string | null;
  recipient_client_name: string | null; purchaser_name: string | null; theirs: boolean | null;
}

export function GiftCardsPanel({ contactId }: { contactId: string }) {
  const { data, error, reload } = usePanelData(
    () => api.get<{ gift_cards: WalletGiftCard[] }>("/pos/gift-cards", { contact_id: contactId })
      .then((d) => d.gift_cards ?? []),
    [contactId],
  );

  return (
    <Panel title="Gift Cards">
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : !data ? (
        <PanelSkeleton rows={2} />
      ) : data.length === 0 ? (
        <PanelEmpty>No gift cards.</PanelEmpty>
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {data.map((g) => {
            const left = Number(g.balance_cents) || 0;
            const spent = left < (Number(g.initial_cents) || 0);
            const forThem = g.theirs === true;
            return (
              <li key={g.id} className="flex flex-wrap items-baseline gap-x-2 px-4 py-2.5">
                <span className="font-mono text-xs text-zinc-300">{g.code}</span>
                <span className={cn("text-sm font-semibold tabular-nums",
                  left > 0 ? "text-emerald-300" : "text-zinc-500")}>
                  {formatMoney(left)}
                </span>
                {spent && (
                  <span className="text-[11px] text-zinc-500">of {formatMoney(Number(g.initial_cents) || 0)}</span>
                )}
                {!g.active && <Badge color="zinc" size="xs">Not active</Badge>}
                <span className="ml-auto text-[11px] text-zinc-500">
                  {forThem
                    ? `bought for them${g.purchaser_name ? ` by ${g.purchaser_name}` : ""}`
                    : `they bought it${g.recipient_client_name || g.recipient_name ? ` for ${g.recipient_client_name || g.recipient_name}` : ""}`}
                  {" · "}{formatDate(g.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ═══ Documents ════════════════════════════════════════════════════════════
const DOC_CATEGORIES: Array<{
  value: ClientDocument["category"];
  label: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  { value: "lab_script", label: "Lab script", icon: FlaskConical },
  { value: "lab_result", label: "Lab result", icon: TestTubes },
  { value: "boulevard_lab", label: "Lab work from Boulevard", icon: FlaskConical },
  { value: "recommendation", label: "Recommendation letter", icon: FileSignature },
  { value: "prescription", label: "Prescription", icon: Pill },
  { value: "medication_list", label: "Medication & supplement list", icon: Pill },
  { value: "consent", label: "Consent", icon: FileSignature },
  { value: "photo", label: "Photo", icon: ImageIcon },
  { value: "id_card", label: "Photo ID", icon: IdCard },
  { value: "insurance", label: "Insurance card", icon: Shield },
  { value: "prior_notes", label: "Earlier notes (Boulevard)", icon: FileSignature },
  { value: "form", label: "Paperwork", icon: FileSignature },
  { value: "medical_info", label: "Medical information", icon: Shield },
  { value: "other", label: "Other", icon: FileIcon },
];

function docCategoryMeta(category: ClientDocument["category"]) {
  return DOC_CATEGORIES.find((c) => c.value === category) ?? DOC_CATEGORIES[DOC_CATEGORIES.length - 1];
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Where one set of labs came from — shown, and changeable, on the row.
 *
 * "So labs from PMD do not get follow up. Labs from us in house get follow
 * up." Saying In House here books the next bloods onto the chart, exactly as
 * it would have at upload time.
 */
function LabSourceChip({ doc, onChanged }: { doc: ClientDocument; onChanged: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = async (lab_source: "in_house" | "pmd") => {
    setSaving(true);
    try {
      await api.patch(`/documents/${doc.id}`, { lab_source });
      toast.success(lab_source === "in_house"
        ? "In House — the next lab work is on their chart"
        : "From PMD — nothing due");
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setSaving(false); }
  };

  if (doc.lab_source && !open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)} title="Change where these came from"
        className={cn(
          "mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold",
          doc.lab_source === "in_house"
            ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
            : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700",
        )}
      >
        {doc.lab_source === "in_house" ? "In House" : "From PMD"}
      </button>
    );
  }
  return (
    <span className="mt-1 flex items-center gap-1">
      <span className="text-[10px] text-zinc-500">These are:</span>
      <button type="button" disabled={saving} onClick={() => void set("in_house")}
        className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50">
        In House
      </button>
      <button type="button" disabled={saving} onClick={() => void set("pmd")}
        className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300 hover:bg-zinc-700 disabled:opacity-50">
        From PMD
      </button>
    </span>
  );
}

export function DocumentsPanel({
  contactId,
  only,
  title = "Documents",
}: {
  contactId: string;
  /** Show just one kind — lets "Lab scripts" and "Lab results" be their own
   *  tabs on the client record, which is how she thinks about them. */
  only?: ClientDocument["category"] | ClientDocument["category"][];
  title?: string;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const kinds = only == null ? null : Array.isArray(only) ? only : [only];
  const kindKey = kinds?.join(",") ?? "";
  const { data: documents, error, reload } = usePanelData(
    () => api.get<{ documents: ClientDocument[] }>("/documents", { contact_id: contactId })
      .then((d) => (kinds ? d.documents.filter((x) => kinds.includes(x.category)) : d.documents)),
    [contactId, kindKey]  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const openDoc = async (d: ClientDocument) => {
    setBusyId(d.id);
    try {
      const r = await api.get<{ url: string }>(`/documents/${d.id}/view-url`);
      window.open(r.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open document");
    } finally {
      setBusyId(null);
    }
  };

  const removeDoc = async (d: ClientDocument) => {
    const ok = await confirm({
      title: "Delete this document?",
      description: <>"{d.title}" will be permanently removed.</>,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document deleted");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel title={title} action={<HeaderAction onClick={() => setUploadOpen(true)} icon={UploadIcon} label="Upload" />}>
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : documents === null ? (
        <PanelSkeleton rows={3} />
      ) : documents.length === 0 ? (
        <PanelEmpty />
      ) : (
        <ul className="max-h-72 divide-y divide-zinc-800/40 overflow-y-auto">
          {documents.map((d) => {
            const meta = docCategoryMeta(d.category);
            const Icon = meta.icon;
            return (
              <li key={d.id} className="flex items-start gap-3 px-4 py-2.5">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-800">
                  <Icon className="h-3.5 w-3.5 text-zinc-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-zinc-200">{d.title}</p>
                  <p className="mt-0.5 truncate text-xs text-zinc-500">
                    {meta.label} · {formatRelative(d.created_at)}
                    {d.collected_on && ` · drawn ${formatDate(d.collected_on, { month: "short", day: "numeric" })}`}
                  </p>
                  {/* "we will then need in the lab results In House / From
                      PMD" — on the row, because which it is decides whether
                      anything is due. Settable here too: the labs already in
                      her file went in before there was a field to say. */}
                  {(d.category === "lab_result" || d.category === "boulevard_lab") && (
                    <LabSourceChip doc={d} onChanged={reload} />
                  )}
                  {/* "the lab section I need to see the note stand out more
                      bolder bigger color" - it was 10px grey and truncated, so
                      "Fasting panel, drawn 08/12" was the first thing lost on
                      a narrow column. Now it reads at the same size as the
                      name, in amber, and wraps instead of being cut off. */}
                  {d.note && (
                    <p className="mt-1 text-xs font-semibold text-amber-300">{d.note}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <IconAction onClick={() => openDoc(d)} icon={ExternalLink} label="Open" disabled={busyId === d.id} />
                  <IconAction onClick={() => removeDoc(d)} icon={Trash2} label="Delete" disabled={busyId === d.id} danger />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {uploadOpen && (
        <UploadDocumentModal
          contactId={contactId}
          defaultCategory={kinds?.[0]}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); reload(); }}
        />
      )}
    </Panel>
  );
}

/**
 * Photo ID and insurance cards, as pictures.
 *
 * "We also need a gallery for uploading photo id and insurance cards." A list
 * of file names is no use for this — the whole point is to glance at the card
 * and check the name matches. So: thumbnails, each opening full size, and a
 * front/back pair reads as two tiles side by side.
 *
 * The thumbnails are signed links that expire, fetched when the panel opens,
 * so an image URL can't be passed around outside the app.
 */
const CARD_KINDS: ClientDocument["category"][] = ["id_card", "insurance"];

export function CardGalleryPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { data: documents, error, reload } = usePanelData(
    () => api.get<{ documents: ClientDocument[] }>("/documents", { contact_id: contactId })
      .then((d) => d.documents.filter((x) => CARD_KINDS.includes(x.category))),
    [contactId],
  );
  const [uploadOpen, setUploadOpen] = useState(false);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  // One signed link per picture. Anything that isn't an image (a scanned PDF
  // of a card, which does happen) keeps the file tile instead.
  useEffect(() => {
    if (!documents) return;
    let alive = true;
    void Promise.all(
      documents
        .filter((d) => (d.content_type ?? "").startsWith("image/") && !urls[d.id])
        .map(async (d) => {
          try {
            const r = await api.get<{ url: string }>(`/documents/${d.id}/view-url`);
            return [d.id, r.url] as const;
          } catch { return null; }
        }),
    ).then((pairs) => {
      if (!alive) return;
      const found = Object.fromEntries(pairs.filter((p): p is readonly [string, string] => p !== null));
      if (Object.keys(found).length > 0) setUrls((prev) => ({ ...prev, ...found }));
    });
    return () => { alive = false; };
  }, [documents]);  // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (d: ClientDocument) => {
    setBusyId(d.id);
    try {
      const r = await api.get<{ url: string }>(`/documents/${d.id}/view-url`);
      window.open(r.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open that");
    } finally { setBusyId(null); }
  };

  const remove = async (d: ClientDocument) => {
    const ok = await confirm({ title: `Remove "${d.title}"?`, confirmLabel: "Remove", danger: true });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await api.delete(`/documents/${d.id}`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that");
    } finally { setBusyId(null); }
  };

  return (
    <Panel
      title="Photo ID & insurance cards"
      action={
        <Button size="sm" variant="outline" leftIcon={<UploadIcon className="h-3.5 w-3.5" />} onClick={() => setUploadOpen(true)}>
          Add a card
        </Button>
      }
    >
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : !documents ? (
        <div className="p-4"><Skeleton className="h-24" /></div>
      ) : documents.length === 0 ? (
        <p className="px-4 py-3 text-sm text-zinc-500">
          No cards on file. Photograph the front and back and add them here.
        </p>
      ) : (
        <div data-card-gallery className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
          {documents.map((d) => (
            <figure key={d.id} data-card-tile className="group relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/60">
              <button
                type="button"
                onClick={() => void open(d)}
                disabled={busyId === d.id}
                className="block w-full"
                title={`Open ${d.title}`}
              >
                {urls[d.id] ? (
                  <img src={urls[d.id]} alt={d.title} className="h-28 w-full object-cover" />
                ) : (
                  <span className="flex h-28 w-full items-center justify-center text-zinc-600">
                    <FileIcon className="h-6 w-6" />
                  </span>
                )}
              </button>
              <figcaption className="flex items-center gap-1 px-2 py-1.5">
                <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-300" title={d.title}>{d.title}</span>
                <Badge color={d.category === "id_card" ? "sky" : "violet"} size="xs">
                  {d.category === "id_card" ? "ID" : "Insurance"}
                </Badge>
                <IconAction onClick={() => void remove(d)} icon={Trash2} label="Remove" disabled={busyId === d.id} danger />
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {uploadOpen && (
        <UploadDocumentModal
          contactId={contactId}
          defaultCategory="id_card"
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); reload(); }}
        />
      )}
    </Panel>
  );
}

function UploadDocumentModal({
  contactId, onClose, onUploaded, defaultCategory,
}: {
  contactId: string;
  /** Pre-set when uploading from a single-category tab. */
  defaultCategory?: ClientDocument["category"];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<ClientDocument["category"]>(defaultCategory ?? "lab_script");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  /**
   * "we will then need in the lab results In House / From PMD. So labs from
   *  PMD do not get follow up. Labs from us in house get follow up"
   *
   * She was already writing it into the note by hand ("Labs from PMD Received
   * Oct 28th, 2026"); as a field the software can read it, in-house labs set
   * the next bloods on the chart and the client's own doctor's labs don't.
   */
  const [labSource, setLabSource] = useState<"" | "in_house" | "pmd">("in_house");
  const [collectedOn, setCollectedOn] = useState("");
  const isLab = category === "lab_result" || category === "boulevard_lab";

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // "So the date is automatically in there under title" - still editable.
    setTitle(documentTitleFor(f.name));
    // Allow re-choosing the same file to fire onChange again.
    e.target.value = "";
  };

  const save = async () => {
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    if (!title.trim()) {
      toast.error("Give the document a title");
      return;
    }
    setSaving(true);
    try {
      const up = await uploadFile(file, {
        path: `client-docs/${contactId}/${makeUploadFilename(file.name)}`,
        bucket: "private",
      });
      await api.post<{ document: ClientDocument }>("/documents", {
        contact_id: contactId,
        title: title.trim(),
        category,
        // Private uploads are referenced by storage KEY — the view-url
        // endpoint mints a fresh signed link per access.
        file_url: up.key,
        content_type: file.type || "application/octet-stream",
        size_bytes: file.size,
        note: note.trim() || undefined,
        ...(isLab && labSource ? { lab_source: labSource } : {}),
        ...(isLab && collectedOn ? { collected_on: collectedOn } : {}),
      });
      toast.success(isLab && labSource === "in_house"
        ? "Labs filed, and the next ones are on their chart"
        : "Document uploaded");
      onUploaded();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Upload document" size="md">
      <div className="space-y-3">
        <input ref={fileRef} type="file" className="hidden" onChange={pick} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-zinc-700 bg-zinc-900/40 px-4 py-6 text-center hover:border-zinc-500 hover:bg-zinc-900/70"
        >
          <UploadIcon className="h-5 w-5 text-zinc-500" />
          {file ? (
            <>
              <span className="max-w-full truncate text-sm text-zinc-200">{file.name}</span>
              <span className="text-xs text-zinc-500">{formatBytes(file.size)}</span>
            </>
          ) : (
            <>
              <span className="text-sm text-zinc-300">Choose a file</span>
              <span className="text-xs text-zinc-500">Up to 25 MB</span>
            </>
          )}
        </button>
        <div>
          <Label htmlFor="doc-title">Title</Label>
          <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title" />
        </div>
        <div>
          <Label htmlFor="doc-category">Category</Label>
          <Select id="doc-category" value={category} onChange={(e) => setCategory(e.target.value as ClientDocument["category"])}>
            {DOC_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
        </div>
        {isLab && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="doc-lab-source">Where did these come from?</Label>
              <Select id="doc-lab-source" value={labSource}
                onChange={(e) => setLabSource(e.target.value as typeof labSource)}>
                {/* Hers by default: all but two of the lab results in her
                    file were drawn here, and PMD is the exception she already
                    writes on the file by hand. */}
                <option value="in_house">In House</option>
                <option value="pmd">From PMD</option>
                <option value="">Not sure yet</option>
              </Select>
              <p className="mt-1 text-[11px] text-zinc-500">
                In House puts the next lab work on their chart. From PMD doesn't.
              </p>
            </div>
            <div>
              <Label htmlFor="doc-collected">Drawn on (optional)</Label>
              <Input id="doc-collected" type="date" value={collectedOn}
                onChange={(e) => setCollectedOn(e.target.value)} />
              <p className="mt-1 text-[11px] text-zinc-500">
                The next lab work is counted from this day. Today if you leave it.
              </p>
            </div>
          </div>
        )}
        <div>
          <Label htmlFor="doc-note">Note (optional)</Label>
          <Input id="doc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Fasting panel, drawn 08/12" />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={!file}>Upload</Button>
      </ModalFooter>
    </Modal>
  );
}

// ═══ Forms ════════════════════════════════════════════════════════════════
const FORM_STATUS: Record<FormRequest["status"], { color: string; label: string }> = {
  sent: { color: "zinc", label: "Sent" },
  viewed: { color: "amber", label: "Opened" },
  completed: { color: "emerald", label: "Completed" },
};

export function ClientFormsPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const me = useMe();
  const { data: requests, error, reload } = usePanelData(
    () => api.get<{ requests: FormRequest[] }>("/forms/requests", { contact_id: contactId }).then((d) => d.requests),
    [contactId]
  );
  const { data: sections } = usePanelData(
    () => api.get<{ sections: FormSection[] }>("/forms").then((d) => d.sections ?? []),
    []
  );
  const [sendOpen, setSendOpen] = useState(false);
  const [viewing, setViewing] = useState<FormRequest | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  /* Her folders: "Renewal (fixed at the top), Initial IV, Initial NAD…"
     Every form files itself into one; a folder with nothing in it stays out
     of the way. */
  const grouped = useMemo(() => {
    if (!requests) return null;
    const order = (sections ?? []).map((x) => x.key);
    const label = new Map((sections ?? []).map((x) => [x.key, x.label]));
    const bySection = new Map<string, FormRequest[]>();
    // On the real time, not letter by letter — "Tue …" sorts above "Sat …".
    for (const r of [...requests].sort(
      (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
    )) {
      const key = r.section || "other";
      const list = bySection.get(key) ?? [];
      list.push(r);
      bySection.set(key, list);
    }
    return [...bySection.entries()]
      .sort((a, b) => {
        const ia = order.indexOf(a[0]), ib = order.indexOf(b[0]);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })
      .map(([key, list]) => ({ key, label: label.get(key) ?? "Other paperwork", list }));
  }, [requests, sections]);

  const copyLink = async (r: FormRequest) => {
    try {
      await navigator.clipboard.writeText(r.url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const resend = async (r: FormRequest) => {
    setResendingId(r.id);
    try {
      await api.post(`/forms/requests/${r.id}/resend`);
      toast.success("Form resent");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to resend");
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Panel title="Forms" action={<HeaderAction onClick={() => setSendOpen(true)} icon={SendIcon} label="Send" />}>
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : grouped === null ? (
        <PanelSkeleton rows={2} />
      ) : grouped.length === 0 ? (
        <PanelEmpty />
      ) : (
        <div className="divide-y divide-zinc-800/40">
          {grouped.map((group) => (
            <div key={group.key} data-form-section={group.key}>
              <p className="bg-zinc-900/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                {group.label}
                <span className="ml-1.5 font-normal normal-case tracking-normal text-zinc-600">
                  {group.list.length}
                </span>
              </p>
              <ul className="divide-y divide-zinc-800/40">
                {group.list.map((r) => {
                  const status = FORM_STATUS[r.status];
                  const reviewed = r.review_status === "reviewed";
                  const awaiting = r.review_status === "needs_review";
                  return (
                    <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="min-w-0 truncate text-sm text-zinc-200">{r.template_name || "Form"}</p>
                          <Badge color={status.color} size="xs">{status.label}</Badge>
                          {reviewed && <Badge color="emerald" size="xs">Reviewed</Badge>}
                          {awaiting && <Badge color="amber" size="xs">Needs review</Badge>}
                        </div>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {formatRelative(r.sent_at)}
                          {reviewed && r.reviewed_by ? ` · reviewed by ${r.reviewed_by}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {/* "click on review answers and the box shows up they
                            review and at the bottom of that box they have a
                            reviewed button" — the row opens the form; it never
                            signs one off in a click without it being read. */}
                        {r.status === "completed" && me?.can_review_forms && (
                          <button
                            type="button"
                            onClick={() => setViewing(r)}
                            title={reviewed ? "Read it again" : "Read it, then sign it off at the bottom"}
                            className={cn(
                              "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px]",
                              reviewed
                                ? "border-emerald-500/50 text-emerald-300"
                                : "border-amber-500/50 text-amber-200 hover:bg-amber-500/10",
                            )}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            {reviewed ? "Reviewed" : "Review answers"}
                          </button>
                        )}
                        <IconAction onClick={() => copyLink(r)} icon={Clipboard} label="Copy link" />
                        {r.status === "completed" ? (
                          <IconAction onClick={() => setViewing(r)} icon={Eye} label="View answers" />
                        ) : (
                          <>
                            {/* "We have some clients who don't do the paperwork -
                                is it possible to have a link to open and fill out
                                for them in the chart under forms?" - opens the
                                client's own form page so the desk can go through
                                it with them. */}
                            <IconAction
                              onClick={() => window.open(r.url, "_blank", "noopener")}
                              icon={ExternalLink}
                              label="Fill it in here"
                            />
                            <IconAction onClick={() => resend(r)} icon={RefreshCw} label="Resend" disabled={resendingId === r.id} />
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      {sendOpen && (
        <SendFormModal
          contactId={contactId}
          onClose={() => setSendOpen(false)}
          onSent={() => { setSendOpen(false); reload(); }}
        />
      )}
      {viewing && (
        <FormAnswersModal
          request={viewing}
          onClose={() => setViewing(null)}
          onReviewed={reload}
        />
      )}
    </Panel>
  );
}

function answerText(v: unknown): string {
  if (Array.isArray(v)) return v.length ? v.map((x) => String(x)).join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

/**
 * Reading a completed form, and signing off on it.
 *
 * "under the forms tab when we click to review (providers only) click on
 *  review answers and the box shows up they review and at the bottom of that
 *  box they have a reviewed button specific to that provider with their name
 *  title saying they reviewed"
 *
 * So the button lives at the BOTTOM of the answers, not on the row: the point
 * is that it is pressed having read the thing, and it says whose signature it
 * is about to put on it. Anyone with the clinical file can open and read a
 * form; only a provider can sign it off.
 */
function FormAnswersModal({
  request, onClose, onReviewed,
}: {
  request: FormRequest;
  onClose: () => void;
  onReviewed: () => void;
}) {
  const me = useMe();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const reviewed = request.review_status === "reviewed";
  const stamp = [me?.name, me?.credential].filter(Boolean).join(" ");

  const sign = async (undo: boolean) => {
    setSaving(true);
    try {
      await api.post(`/forms/requests/${request.id}/review${undo ? "?undo=1" : ""}`);
      toast.success(undo ? "Marked as not reviewed" : `Reviewed by ${stamp}`,
        undo ? undefined : "Logged in the clinical file.");
      onReviewed();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't mark that reviewed");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: FormField) => {
    if (f.type === "statement") return null;
    if (f.type === "heading") {
      return (
        <p key={f.id} className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 first:pt-0">
          {f.label || f.text || ""}
        </p>
      );
    }
    return (
      <div key={f.id}>
        <p className="text-xs text-zinc-500">{f.label}</p>
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-200">{answerText(request.answers[f.id])}</p>
      </div>
    );
  };

  return (
    <Modal open onClose={onClose} title={request.template_name || "Form answers"} size="lg">
      <div className="max-h-[60vh] space-y-3 overflow-y-auto">
        {request.fields.length === 0 ? (
          <p className="text-sm text-zinc-500">This form has no fields.</p>
        ) : (
          request.fields.map(renderField)
        )}
        {request.signature && (
          <div className="flex items-center gap-2 border-t border-zinc-800/60 pt-3 text-xs text-zinc-400">
            <FileSignature className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            Signed by <span className="font-medium text-zinc-200">{request.signature.name}</span>
            {" · "}{formatDate(request.signature.signed_at)}
          </div>
        )}
      </div>
      {/* Who has already signed it off, under the answers where it belongs. */}
      {reviewed && request.reviewed_by && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          Reviewed by <span className="font-medium">{request.reviewed_by}</span>
          {request.reviewed_at && <>{" · "}{formatDate(request.reviewed_at)}</>}
        </div>
      )}
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        {request.status === "completed" && me?.can_review_forms && (
          reviewed ? (
            <Button variant="secondary" loading={saving} onClick={() => void sign(true)}>
              Undo reviewed
            </Button>
          ) : (
            <Button
              loading={saving}
              onClick={() => void sign(false)}
              leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
            >
              Reviewed by {stamp || "me"}
            </Button>
          )
        )}
      </ModalFooter>
    </Modal>
  );
}

/**
 * What the medical director has signed off on, on this client.
 *
 * "then over in the chart under forms it will have medical Director Reviewed
 *  spot that logs time/day … that box will show (seperate box in the form tab
 *  in the chart labeled Medical director) reviewed by with title and name of
 *  MD"
 *
 * Her sub-sections under Forms are Forms, Medical Director, Paperwork on file.
 * This is the middle one: the charts she has read, with her note, her name and
 * letters, and when she did it. A chart still waiting shows here too, so the
 * answer to "has she seen this client?" is on one screen.
 */
export function MedicalDirectorPanel({ contactId }: { contactId: string }) {
  const { data, error, reload } = usePanelData(
    () => api.get<{ charts: Chart[] }>("/charts", { contact_id: contactId })
      .then((d) => (d.charts ?? []).filter(
        (c) => c.review_status === "reviewed" || c.review_status === "needs_review",
      )),
    [contactId],
  );

  return (
    <Panel title="Medical Director">
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : !data ? (
        <PanelSkeleton rows={2} />
      ) : data.length === 0 ? (
        <PanelEmpty>Nothing has gone to the medical director for this client.</PanelEmpty>
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {data.map((c) => {
            const done = c.review_status === "reviewed";
            return (
              <li key={c.id} className="px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="min-w-0 truncate text-sm text-zinc-200">{c.title}</p>
                  {done
                    ? <Badge color="emerald" size="xs">Reviewed</Badge>
                    : <Badge color="amber" size="xs">Waiting</Badge>}
                </div>
                {done ? (
                  <p className="mt-0.5 text-xs text-zinc-400">
                    Reviewed by <span className="font-medium text-zinc-200">{c.reviewed_by}</span>
                    {c.reviewed_at && <> · {formatDate(c.reviewed_at)} {formatTime(c.reviewed_at)}</>}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-zinc-500">
                    On her list since {formatDate(c.signed_at ?? c.created_at)}
                    {/* Whether it was sent to her or picked at random, so the
                        sample is visibly doing its job. */}
                    {c.review_source === "sample" ? " · picked at random"
                      : c.review_requested_by ? ` · sent by ${c.review_requested_by}` : ""}
                  </p>
                )}
                {c.review_note && (
                  <p className="mt-1 whitespace-pre-wrap rounded-md bg-zinc-900/50 px-2.5 py-1.5 text-xs text-zinc-300">
                    {c.review_note}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Send one form, or several.
 *
 * "is it possible to send more then one with the send form can I choose which
 *  to send 2 or three if needed"
 *
 * Ticking three does NOT send three texts - the server creates all of them
 * and then sends one text and one email carrying every link.
 */
function SendFormModal({
  contactId, onClose, onSent,
}: {
  contactId: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const toast = useToast();
  const [templates, setTemplates] = useState<FormTemplate[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<{ templates: FormTemplate[] }>("/forms")
      .then((d) => {
        if (cancelled) return;
        setTemplates(d.templates.filter((t) => !t.archived));
      })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, []);

  const toggle = (id: string) =>
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const send = async () => {
    if (chosen.length === 0) return;
    setSending(true);
    try {
      const r = await api.post<{ requests: { requestId: string }[]; delivered: boolean; channel: string; error?: string }>(
        "/forms/requests/batch",
        { form_template_ids: chosen, contact_id: contactId }
      );
      const n = r.requests?.length ?? chosen.length;
      if (r.delivered) {
        toast.success(n === 1 ? `Sent by ${r.channel}` : `${n} forms sent by ${r.channel}`,
          n === 1 ? undefined : "One message with all of the links.");
      } else {
        toast.error(r.error || "Created, but couldn't deliver - copy the links from the list");
      }
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to send");
      setSending(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={chosen.length > 1 ? `Send ${chosen.length} forms` : "Send a form"} size="sm">
      <div className="space-y-3">
        {templates === null ? (
          <Skeleton className="h-9 w-full" />
        ) : templates.length === 0 ? (
          <p className="text-xs text-zinc-500">No form templates yet - create one on the Forms page first.</p>
        ) : (
          <>
            <Label>Which forms?</Label>
            <ul className="max-h-72 divide-y divide-zinc-800/40 overflow-y-auto rounded-md border border-zinc-800">
              {templates.map((t) => (
                <li key={t.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-900/50">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500 accent-indigo-500"
                      checked={chosen.includes(t.id)}
                      onChange={() => toggle(t.id)}
                    />
                    <span className="min-w-0 truncate">{t.name}</span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="text-xs text-zinc-500">
              {chosen.length > 1
                ? `${chosen.length} chosen. They go out together in one text and one email.`
                : "Tick as many as you need."}
            </p>
          </>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={send} loading={sending} disabled={chosen.length === 0} leftIcon={<SendIcon className="h-3.5 w-3.5" />}>
          {chosen.length > 1 ? `Send ${chosen.length}` : "Send"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ═══ Memberships & account credit ═════════════════════════════════════════
/** One label per situation the front desk has to act on differently. */
function membershipState(s: MembershipSubscription): { color: string; label: string } {
  if (s.status === "canceled") return { color: "zinc", label: "Canceled" };
  if (s.status === "past_due") {
    return s.payment_failed_at ? { color: "rose", label: "Card declined" } : { color: "amber", label: "Renewal due" };
  }
  if (s.status === "pending") return { color: "zinc", label: "Pending" };
  if (s.cancel_at_period_end) return { color: "amber", label: "Ending" };
  return s.auto_pay ? { color: "emerald", label: "Auto-pay" } : { color: "emerald", label: "Active" };
}

function cycleLabel(interval: string | undefined): string {
  return interval === "year" ? "yr" : interval === "two_months" ? "2 mo" : "mo";
}

function copyText(text: string, toast: ReturnType<typeof useToast>) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success("Link copied", "Text it to the client or paste it into a message."))
    .catch(() => toast.error("Couldn't copy - open it and copy the address instead"));
}

function SmallButton({
  onClick, children, disabled, danger,
}: {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ring-1 ring-inset disabled:opacity-40",
        danger
          ? "text-zinc-400 ring-zinc-700 hover:bg-rose-500/10 hover:text-rose-300 hover:ring-rose-500/30"
          : "text-zinc-300 ring-zinc-700 hover:bg-zinc-800 hover:text-zinc-100",
      )}
    >
      {children}
    </button>
  );
}

export function MembershipPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, error, reload } = usePanelData(
    async () => {
      const [subs, credit] = await Promise.all([
        api.get<{ subscriptions: MembershipSubscription[] }>("/pos/memberships/subscriptions", { contact_id: contactId }),
        api.get<{ balance_cents: number; entries: CreditEntry[] }>("/pos/credit", { contact_id: contactId }),
      ]);
      return {
        subscriptions: subs.subscriptions ?? [],
        balanceCents: Number(credit.balance_cents ?? 0),
        entries: credit.entries ?? [],
      };
    },
    [contactId]
  );
  const [creditOpen, setCreditOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [link, setLink] = useState<{ title: string; body: string; url: string } | null>(null);

  const act = async (s: MembershipSubscription, fn: () => Promise<void>) => {
    setBusyId(s.id);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusyId(null);
    }
  };

  const cancelSubscription = async (s: MembershipSubscription) => {
    const name = s.membership_name || "This membership";
    const ok = await confirm({
      title: "Cancel this membership?",
      description: s.auto_pay
        ? <>&ldquo;{name}&rdquo; stops renewing and their card won&apos;t be charged again. They keep their perks until {s.current_period_end ? formatDate(s.current_period_end) : "the end of the paid period"}.</>
        : <>&ldquo;{name}&rdquo; ends now. Credit and sessions already granted stay on the account.</>,
      confirmLabel: "Cancel membership",
      danger: true,
    });
    if (!ok) return;
    await act(s, async () => {
      await api.post(`/pos/memberships/subscriptions/${s.id}/cancel`);
      toast.success(s.auto_pay ? "Membership set to end - no more charges" : "Membership canceled");
      reload();
    });
  };

  const resume = (s: MembershipSubscription) => act(s, async () => {
    await api.post(`/pos/memberships/subscriptions/${s.id}/resume`);
    toast.success("Membership will keep renewing");
    reload();
  });

  const setUpAutoPay = (s: MembershipSubscription) => act(s, async () => {
    const d = await api.post<{ url: string }>(`/pos/memberships/subscriptions/${s.id}/autopay`);
    setLink({
      title: "Auto-pay link",
      body: `The client enters their card once. Nothing is charged today. From ${s.current_period_end ? formatDate(s.current_period_end) : "their renewal date"} it renews on that card automatically.`,
      url: d.url,
    });
    reload();
  });

  const balance = data?.balanceCents ?? 0;
  const lastEntry = data?.entries[0] ?? null;
  // Newest first, but anything still running outranks a canceled plan.
  const subscriptions = data
    ? [...data.subscriptions].sort((a, b) => {
        const rank = (s: MembershipSubscription) => (s.status === "canceled" ? 1 : 0);
        return rank(a) - rank(b);
      })
    : null;

  return (
    <Panel
      title="Memberships & credit"
      action={data ? <HeaderAction onClick={() => setCreditOpen(true)} icon={Plus} label="Add credit" /> : undefined}
    >
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : subscriptions === null ? (
        <PanelSkeleton rows={3} />
      ) : subscriptions.length === 0 && balance === 0 ? (
        <PanelEmpty>No membership. Account credit {formatMoney(0)}. Sell one at Checkout.</PanelEmpty>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Account credit</p>
              <p
                className={cn(
                  "mt-0.5 text-lg font-semibold tabular-nums",
                  balance > 0 ? "text-emerald-400" : balance < 0 ? "text-rose-300" : "text-zinc-400",
                )}
              >
                {formatMoney(balance)}
              </p>
            </div>
            {lastEntry && (
              <p className="min-w-0 shrink text-right text-[11px] text-zinc-500">
                <span className="block truncate">{lastEntry.reason || "Adjustment"}</span>
                <span className="block">{formatRelative(lastEntry.created_at)}</span>
              </p>
            )}
          </div>
          {subscriptions.length === 0 ? (
            <p className="border-t border-zinc-800/40 px-4 py-3 text-xs text-zinc-500">No membership. Sell one at Checkout.</p>
          ) : (
            <ul className="divide-y divide-zinc-800/40 border-t border-zinc-800/40">
              {subscriptions.map((s) => {
                const state = membershipState(s);
                const price = s.price_cents != null ? `${formatMoney(Number(s.price_cents))}/${cycleLabel(s.interval)}` : null;
                const end = s.current_period_end ? formatDate(s.current_period_end) : null;
                const detail =
                  s.status === "canceled" ? (s.canceled_at ? `ended ${formatDate(s.canceled_at)}` : "ended")
                  : s.status === "past_due" && s.payment_failed_at ? `card declined ${formatDate(s.payment_failed_at)} · Stripe is retrying it`
                  : s.status === "past_due" ? (end ? `renewal was due ${end}` : "renewal due")
                  : s.cancel_at_period_end ? (end ? `ends ${end} · no more charges` : "ending")
                  : s.auto_pay ? (end ? `renews ${end} on their card` : "renews on their card")
                  : end ? `paid through ${end} · paid by hand` : "paid by hand";
                const busy = busyId === s.id;
                const lateEnough = !!s.current_period_end &&
                  new Date(s.current_period_end).getTime() > Date.now() + 49 * 3600_000;
                const paid = (s.cycles ?? []).slice(0, 4);
                return (
                  <li key={s.id} className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <p className="min-w-0 truncate text-sm text-zinc-200">
                        {s.membership_name || "Membership"}
                        {s.practice && <Badge color="amber" size="xs" className="ml-2">Practice</Badge>}
                      </p>
                      <Badge color={state.color} size="xs">{state.label}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {price}{price && detail ? " · " : ""}{detail}
                    </p>
                    {paid.length > 0 && (
                      <p className="mt-0.5 text-[11px] text-zinc-600">
                        Paid {paid.map((c) => `${formatDate(c.paid_at)} ${formatMoney(Number(c.amount_cents))}`).join(" · ")}
                      </p>
                    )}
                    {s.status !== "canceled" && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {s.status === "past_due" && s.pay_url && (
                          <>
                            <SmallButton onClick={() => copyText(s.pay_url!, toast)}>
                              <Clipboard className="h-3 w-3" /> Copy payment link
                            </SmallButton>
                            <a
                              href={s.pay_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-zinc-300 ring-1 ring-inset ring-zinc-700 hover:bg-zinc-800 hover:text-zinc-100"
                            >
                              <ExternalLink className="h-3 w-3" /> Open
                            </a>
                          </>
                        )}
                        {s.status === "past_due" && !s.auto_pay && (
                          <Link
                            to={`/checkout?contact_id=${s.contact_id}&membership_id=${s.membership_id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-indigo-500/15 px-2 py-1 text-[11px] font-medium text-indigo-300 ring-1 ring-inset ring-indigo-500/30 hover:bg-indigo-500/25"
                          >
                            <RefreshCw className="h-3 w-3" /> Renew at checkout
                          </Link>
                        )}
                        {s.status === "active" && !s.auto_pay && (
                          s.setup_url ? (
                            <SmallButton onClick={() => copyText(s.setup_url!, toast)}>
                              <Clipboard className="h-3 w-3" /> Copy auto-pay link
                            </SmallButton>
                          ) : lateEnough ? (
                            <SmallButton onClick={() => void setUpAutoPay(s)} disabled={busy}>
                              <RefreshCw className="h-3 w-3" /> Set up auto-pay
                            </SmallButton>
                          ) : null
                        )}
                        {s.cancel_at_period_end ? (
                          <SmallButton onClick={() => void resume(s)} disabled={busy}>
                            <RefreshCw className="h-3 w-3" /> Keep renewing
                          </SmallButton>
                        ) : (
                          <SmallButton onClick={() => void cancelSubscription(s)} disabled={busy} danger>
                            <Ban className="h-3 w-3" /> Cancel
                          </SmallButton>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      {creditOpen && (
        <AddCreditModal
          contactId={contactId}
          onClose={() => setCreditOpen(false)}
          onSaved={() => { setCreditOpen(false); reload(); }}
        />
      )}
      {link && (
        <Modal open onClose={() => setLink(null)} title={link.title} size="sm">
          <p className="text-sm text-zinc-400">{link.body}</p>
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-300">{link.url}</span>
            <button
              type="button"
              onClick={() => copyText(link.url, toast)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              aria-label="Copy link"
            >
              <Clipboard className="h-4 w-4" />
            </button>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              aria-label="Open link"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <ModalFooter>
            <Button onClick={() => setLink(null)}>Done</Button>
          </ModalFooter>
        </Modal>
      )}
    </Panel>
  );
}

function AddCreditModal({
  contactId, onClose, onSaved,
}: {
  contactId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  /**
   * Her own list of reasons, picked rather than typed.
   *
   *   "I also need to be able to add credit and give reasons drop box with
   *    Return Consultation Fee And a way to edit add/delete reasons"
   *
   * Typed free-hand, the same reason is written five ways and no report can
   * group it. "Something else" is still there so nobody is ever stuck.
   */
  const [reasons, setReasons] = useState<{ id: string; label: string }[] | null>(null);
  const [editingList, setEditingList] = useState(false);
  const [newReason, setNewReason] = useState("");
  const OTHER = "__other__";

  const loadReasons = useCallback(async () => {
    try {
      const r = await api.get<{ reasons: { id: string; label: string }[] }>("/pos/credit-reasons");
      setReasons(r.reasons ?? []);
    } catch { setReasons([]); }
  }, []);
  useEffect(() => { void loadReasons(); }, [loadReasons]);

  const dollars = parseFloat(amount);
  const cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;

  const addReason = async () => {
    const label = newReason.trim();
    if (!label) return;
    try {
      await api.post("/pos/credit-reasons", { label });
      setNewReason("");
      await loadReasons();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that reason");
    }
  };

  const save = async () => {
    if (cents === 0) {
      toast.error("Enter an amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/pos/credit", {
        contact_id: contactId,
        amount_cents: cents,
        reason: reason.trim() || undefined,
      });
      toast.success(cents > 0 ? `${formatMoney(cents)} credit added` : `${formatMoney(-cents)} deducted`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save credit");
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add account credit" size="sm">
      <div className="space-y-3">
        <div>
          <Label htmlFor="credit-amount" required>Amount</Label>
          <Input
            id="credit-amount"
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="50.00"
            autoFocus
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Credit comes off their next visit automatically. Use a negative amount (e.g. -25) to take credit back.
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="credit-reason">Reason</Label>
            <button
              type="button"
              onClick={() => setEditingList((v) => !v)}
              className="text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
            >
              {editingList ? "Done" : "Edit list"}
            </button>
          </div>
          {reasons === null ? (
            <PanelSkeleton rows={1} />
          ) : (
            <>
              <Select
                id="credit-reason"
                value={reasons.some((r) => r.label === reason) ? reason : reason ? OTHER : ""}
                onChange={(e) => setReason(e.target.value === OTHER ? " " : e.target.value)}
              >
                <option value="">Choose a reason…</option>
                {reasons.map((r) => <option key={r.id} value={r.label}>{r.label}</option>)}
                <option value={OTHER}>Something else…</option>
              </Select>
              {!reasons.some((r) => r.label === reason) && reason !== "" && (
                <Input
                  className="mt-1.5"
                  value={reason.trim()}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Say why in your own words"
                  autoFocus
                />
              )}
            </>
          )}

          {editingList && reasons !== null && (
            <div className="mt-2 space-y-1.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2">
              {reasons.map((r) => (
                <div key={r.id} className="flex items-center gap-1.5">
                  <Input
                    value={r.label}
                    onChange={(e) => setReasons((prev) =>
                      (prev ?? []).map((x) => x.id === r.id ? { ...x, label: e.target.value } : x))}
                    onBlur={async (e) => {
                      const label = e.target.value.trim();
                      if (!label || label === r.label.trim()) return;
                      try { await api.patch(`/pos/credit-reasons/${r.id}`, { label }); await loadReasons(); }
                      catch (err) { toast.error(err instanceof ApiError ? err.message : "Couldn't rename that"); void loadReasons(); }
                    }}
                    className="h-7 text-xs"
                  />
                  <button
                    type="button"
                    title={`Remove ${r.label}`}
                    aria-label={`Remove ${r.label}`}
                    onClick={async () => {
                      try {
                        await api.delete(`/pos/credit-reasons/${r.id}`);
                        if (reason === r.label) setReason("");
                        await loadReasons();
                      } catch (err) { toast.error(err instanceof ApiError ? err.message : "Couldn't remove that"); }
                    }}
                    className="shrink-0 rounded p-1 text-zinc-600 hover:bg-zinc-800 hover:text-rose-400"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-1.5 pt-0.5">
                <Input
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="Add a reason…"
                  className="h-7 text-xs"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addReason(); } }}
                />
                <Button size="sm" variant="secondary" className="h-7 shrink-0 text-[11px]" onClick={() => void addReason()}>
                  Add
                </Button>
              </div>
              {/* Nothing here changes a credit she has already given: a
                  reason she removes stays written on the ones it was used
                  for. */}
              <p className="pt-0.5 text-[10px] text-zinc-600">
                Credits already given keep the reason they were given with.
              </p>
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={cents === 0}>
          {cents < 0 ? "Deduct credit" : "Add credit"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ═══ Packages & visits used ═══════════════════════════════════════════════
/** What a voucher is for: the line's own name, its treatment, or the choice. */
function voucherFor(b: PackageBalance): string {
  if (b.item_label?.trim()) return b.item_label.trim();
  // A product voucher names the item - "add the ability to add products to
  // packages and memberships" - so the wallet reads "Hair Renew", not blank.
  if (b.product_name) return b.product_name;
  const names = b.choice_names ?? [];
  if (names.length === 1) return names[0]!;
  if (names.length > 1) return `any of ${names.length} treatments`;
  return b.service_name ?? "";
}

export function PackageHistoryPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, error, reload } = usePanelData(
    async () => {
      const [balances, redemptions] = await Promise.all([
        api.get<{ balances: PackageBalance[] }>("/pos/package-balances", { contact_id: contactId }),
        api.get<{ redemptions: PackageRedemption[] }>("/pos/package-redemptions", { contact_id: contactId }),
      ]);
      return { balances: balances.balances ?? [], redemptions: redemptions.redemptions ?? [] };
    },
    [contactId]
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  const balances = data?.balances ?? [];
  const redemptions = data?.redemptions ?? [];

  /**
   * "shows how they are receiving medications" — a posted line is not used at
   * a visit and is not handed over at the counter, so it sits here saying it
   * is to go until somebody says it went.
   */
  const markMailed = async (b: PackageBalance) => {
    const ok = await confirm({
      title: `Mark ${b.package_name || "this"} as posted?`,
      description: "This records that it went out today, on their file.",
      confirmLabel: "It has gone",
    });
    if (!ok) return;
    setBusyId(b.id);
    try {
      await api.post(`/pos/package-balances/${b.id}/mailed`);
      toast.success("Recorded as posted");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to record that");
    } finally {
      setBusyId(null);
    }
  };

  const takeHomeRest = async (b: PackageBalance, remaining: number) => {
    const ok = await confirm({
      title: `Take home the rest of ${b.package_name || "this package"}?`,
      description: <>This hands over all {remaining} remaining {remaining === 1 ? "dose" : "doses"} today and records it on their file. The balance goes to zero.</>,
      confirmLabel: `Hand over ${remaining}`,
    });
    if (!ok) return;
    setBusyId(b.id);
    try {
      await api.post(`/pos/package-balances/${b.id}/take-home`);
      toast.success(`${remaining} ${remaining === 1 ? "dose" : "doses"} recorded as taken home`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to record the take-home");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel title="Packages and Memberships purchased">
      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : data === null ? (
        <PanelSkeleton rows={3} />
      ) : balances.length === 0 && redemptions.length === 0 ? (
        <PanelEmpty>No packages bought yet.</PanelEmpty>
      ) : (
        <>
          {balances.length > 0 && (
            <ul className="divide-y divide-zinc-800/40">
              {balances.map((b) => {
                const total = Number(b.sessions_total) || 0;
                const remaining = Math.max(0, Number(b.sessions_remaining) || 0);
                const pct = total > 0 ? Math.min(100, Math.max(0, (remaining / total) * 100)) : 0;
                return (
                  <li key={b.id} className="px-4 py-2.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="min-w-0 truncate text-sm text-zinc-200">
                        {b.package_name || "Package"}
                        {voucherFor(b) && <span className="text-zinc-500"> · {voucherFor(b)}</span>}
                        {b.practice && <Badge color="amber" size="xs" className="ml-2">Practice</Badge>}
                      </p>
                      {b.mailed_at ? (
                        <Badge color="sky" size="xs">Posted</Badge>
                      ) : b.fulfilment === "mail_out" ? (
                        <Badge color="amber" size="xs">To post</Badge>
                      ) : b.taken_home_at ? (
                        <Badge color="teal" size="xs">Taken home</Badge>
                      ) : (
                        <p className={cn("shrink-0 text-xs tabular-nums", remaining === 0 ? "text-zinc-500" : "text-emerald-300")}>
                          {remaining} of {total} left
                        </p>
                      )}
                    </div>
                    {!b.taken_home_at && (
                      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-zinc-800">
                        <div
                          className={cn("h-full rounded-full", remaining === 0 ? "bg-zinc-700" : "bg-emerald-500/80")}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="text-[10px] text-zinc-500">
                        bought {formatDate(b.purchased_at)}
                        {b.taken_home_at && !b.mailed_at ? ` · taken home ${formatDate(b.taken_home_at)}` : ""}
                        {b.mailed_at ? ` · posted ${formatDate(b.mailed_at)}` : ""}
                      </p>
                      {b.fulfilment === "mail_out" && !b.mailed_at ? (
                        <button
                          type="button"
                          onClick={() => void markMailed(b)}
                          disabled={busyId === b.id}
                          className="shrink-0 text-[11px] font-medium text-sky-300 hover:text-sky-200 disabled:opacity-40"
                        >
                          Mark as posted
                        </button>
                      ) : (b.package_take_home || b.fulfilment === "take_home") && !b.taken_home_at && remaining > 0 && (
                        <button
                          type="button"
                          onClick={() => void takeHomeRest(b, remaining)}
                          disabled={busyId === b.id}
                          className="shrink-0 text-[11px] font-medium text-teal-300 hover:text-teal-200 disabled:opacity-40"
                        >
                          Take home the rest
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className={cn(balances.length > 0 && "border-t border-zinc-800/40")}>
            <p className="px-4 pb-1 pt-3 text-[10px] uppercase tracking-wider text-zinc-500">Used</p>
            {redemptions.length === 0 ? (
              <p className="px-4 pb-3 text-xs text-zinc-500">No visits redeemed yet.</p>
            ) : (
              <ul className="max-h-56 divide-y divide-zinc-800/40 overflow-y-auto">
                {redemptions.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-4 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs text-zinc-300">
                        {formatDate(r.created_at)} · {r.service_name || r.package_name || "Visit"}
                        {r.take_home && <span className="text-teal-300"> · taken home</span>}
                      </p>
                      <p className="mt-0.5 truncate text-[10px] text-zinc-500">
                        {r.invoice_number ? `Invoice ${r.invoice_number}` : r.take_home ? "Handed over from their package" : "No invoice"}
                        {r.sessions > 1 ? ` · ${r.sessions} ${r.take_home ? "doses" : "sessions"}` : ""}
                      </p>
                      {/* A void says so, and says why. */}
                      {r.note && (
                        <p className="mt-0.5 text-[10px] font-medium text-amber-300">{r.note}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

// ─── Client stat strip ─────────────────────────────────────────────────────
//
// [07:15] Boulevard heads every client profile with a row of numbers she
// reads before she speaks to anyone: appointments, show rate, average time
// between visits, average visit value. Same idea, minus the walk-in count
// her practice barely uses, plus what they've spent all told.

interface ClientStats {
  visits: number;
  upcoming: number;
  no_shows: number;
  cancelled: number;
  show_rate: number | null;
  avg_revisit_weeks: number | null;
  avg_visit_value: number;
  lifetime_spend: number;
  last_visit: string | null;
  /** When their consultation runs out. null = no renewal rule, or they have
   *  never had one. */
  consult_due_at: string | null;
  consult_months: number | null;
}

export function ClientStatStrip({ contactId }: { contactId: string }) {
  const { data, error, reload } = usePanelData<{ stats: ClientStats }>(
    () => api.get<{ stats: ClientStats }>(`/frontdesk/clients/${contactId}/stats`),
    [contactId],
  );

  if (error) return <PanelErrorRow message="Couldn't load this client's numbers" onRetry={reload} />;
  if (!data) return <Skeleton className="h-16" />;
  const s = data?.stats;
  if (!s) return null;

  const cells: { label: string; value: string; tone?: string }[] = [
    { label: "Visits", value: String(s.visits) },
    {
      label: "Show rate",
      value: s.show_rate === null ? "—" : `${s.show_rate}%`,
      tone: s.show_rate !== null && s.show_rate < 80 ? "text-amber-300" : undefined,
    },
    {
      label: "Comes back every",
      value: s.avg_revisit_weeks === null ? "—" : `${s.avg_revisit_weeks} wk`,
    },
    { label: "Avg visit", value: formatMoney(s.avg_visit_value) },
    { label: "Spent", value: formatMoney(s.lifetime_spend) },
    { label: "Booked ahead", value: String(s.upcoming) },
  ];
  // "Can we automatically add that in so there is a reminder of when renewal
  // consults are needed" — on the client's own file, where the desk is
  // already looking when they ring.
  if (s.consult_due_at) {
    const due = new Date(s.consult_due_at);
    const lapsed = due.getTime() < Date.now();
    cells.push({
      label: lapsed ? "Consult lapsed" : "Consult renews",
      value: formatDate(s.consult_due_at),
      tone: lapsed ? "text-rose-300" : due.getTime() < Date.now() + 30 * 86400000 ? "text-amber-300" : undefined,
    });
  }

  return (
    <div data-stat-strip className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-800/70 sm:grid-cols-7">
      {cells.map((c) => (
        <div key={c.label} className="bg-zinc-900/60 px-3 py-2 text-center">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">{c.label}</p>
          <p className={cn("mt-0.5 text-sm font-semibold tabular-nums text-zinc-100", c.tone)}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Alerts, and the two dated follow-ups ──────────────────────────────────
//
// "Matt can we have a section for alerts under coming up in the overview page
//  … we should be able to add any alert (there now) … Then we need also
//  Follow up Lab Work / Follow up Consultations. And be able to change edit or
//  create them in this area. They do show in the appointment box but need them
//  here as well"
//
// One list, under Coming up, holding everything outstanding for this person:
//  • the scheduling alert that was already there [27:41], still the one that
//    rides the front-desk card;
//  • as many other alerts as she likes;
//  • the next lab work and the next consultation, as dates.
//
// The two dates are the same dates as the ones in the appointment box — edit
// either and both move. Both can also fill themselves in: a completed
// consultation sets the next one six months out, and in-house labs set the
// next bloods. A date she has typed is never overwritten by the software.

interface ClientAlert {
  id: string;
  kind: "alert" | "followup_lab" | "followup_consult";
  body: string;
  due_date: string | null;
  source: string;
  due_now: boolean;
  because: string | null;
  appointment_id: string | null;
  created_by_name: string | null;
  created_at: string;
}

const ALERT_KIND_LABEL: Record<ClientAlert["kind"], string> = {
  alert: "Alert",
  followup_lab: "Follow up Lab Work",
  followup_consult: "Follow up Consultations",
};
const ALERT_KIND_ICON: Record<ClientAlert["kind"], ComponentType<{ className?: string }>> = {
  alert: AlertTriangle,
  followup_lab: FlaskConical,
  followup_consult: Stethoscope,
};

/** The whole row, in one line, the way she would read it out. */
function alertHeadline(a: ClientAlert): string {
  if (a.kind === "alert") return a.body;
  return a.due_date ? formatDate(a.due_date, { month: "short", day: "numeric", year: "numeric" }) : "No date yet";
}

export function AlertsPanel({
  contactId, schedulingAlert, onSchedulingAlertSaved,
}: {
  contactId: string;
  schedulingAlert: string | null;
  onSchedulingAlertSaved: (v: string | null) => void;
}) {
  const toast = useToast();
  const { data: alerts, error, reload } = usePanelData(
    () => api.get<{ alerts: ClientAlert[] }>(`/clinical/${contactId}/alerts`).then((d) => d.alerts),
    [contactId],
  );
  /** null = closed, "scheduling" = the alert on the contact, else a row id. */
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const kindsTaken = new Set((alerts ?? []).map((a) => a.kind));

  const clearScheduling = async () => {
    setBusy("scheduling");
    try {
      await api.patch(`/contacts/${contactId}`, { scheduling_alert: null });
      onSchedulingAlertSaved(null);
      toast.success("Alert cleared");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't clear that");
    } finally { setBusy(null); }
  };

  const clear = async (a: ClientAlert) => {
    setBusy(a.id);
    try {
      await api.patch(`/clinical/alerts/${a.id}`, { status: "done" });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't clear that");
    } finally { setBusy(null); }
  };

  return (
    <Panel
      title="Alerts"
      action={adding ? undefined : <HeaderAction onClick={() => { setAdding(true); setEditing(null); }} icon={Plus} label="Add" />}
    >
      {adding && (
        <div className="border-b border-zinc-800/40 px-4 py-3">
          <AlertEditor
            contactId={contactId}
            taken={kindsTaken}
            hasSchedulingAlert={Boolean((schedulingAlert ?? "").trim())}
            onSchedulingAlertSaved={onSchedulingAlertSaved}
            onClose={() => setAdding(false)}
            onSaved={() => { setAdding(false); reload(); }}
          />
        </div>
      )}

      {error ? (
        <PanelErrorRow message={error} onRetry={reload} />
      ) : alerts === null ? (
        <PanelSkeleton rows={2} />
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {/* The scheduling alert first: it is the one that shows on the front
              desk every time this client is booked. */}
          {(schedulingAlert ?? "").trim() && (
            <li className="px-4 py-2.5">
              {editing === "scheduling" ? (
                <SchedulingAlertEditor
                  contactId={contactId}
                  value={schedulingAlert ?? ""}
                  onClose={() => setEditing(null)}
                  onSaved={(v) => { onSchedulingAlertSaved(v); setEditing(null); }}
                />
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-300" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-300">
                      Scheduling alert
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-100">{schedulingAlert}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">
                      Shown on the front desk every time they are booked.
                    </p>
                  </div>
                  <IconAction onClick={() => { setEditing("scheduling"); setAdding(false); }} icon={Pencil} label="Change this alert" />
                  {/* It used to take opening the editor, emptying the box and saving
                      — "I'm not able to delete schedule alert for Debra Corrigan".
                      One tap, same as every other line on this list. */}
                  <IconAction
                    onClick={() => void clearScheduling()}
                    icon={CheckCircle2}
                    label="Clear this alert"
                    disabled={busy === "scheduling"}
                  />
                </div>
              )}
            </li>
          )}

          {alerts.map((a) => {
            const Icon = ALERT_KIND_ICON[a.kind];
            const hot = a.kind === "alert" || a.due_now;
            return (
              <li key={a.id} className="px-4 py-2.5">
                {editing === a.id ? (
                  <AlertEditor
                    contactId={contactId}
                    existing={a}
                    taken={kindsTaken}
                    hasSchedulingAlert={Boolean((schedulingAlert ?? "").trim())}
                    onSchedulingAlertSaved={onSchedulingAlertSaved}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", hot ? "text-rose-300" : "text-sky-300")} />
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-[10px] font-semibold uppercase tracking-wider",
                        hot ? "text-rose-300" : "text-sky-300")}>
                        {ALERT_KIND_LABEL[a.kind]}
                        {a.kind !== "alert" && a.due_now && " · due now"}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-100">{alertHeadline(a)}</p>
                      {a.because && <p className="mt-0.5 text-[11px] text-zinc-500">{a.because}</p>}
                    </div>
                    <IconAction onClick={() => { setEditing(a.id); setAdding(false); }} icon={Pencil} label="Change this" />
                    <IconAction
                      onClick={() => void clear(a)}
                      icon={CheckCircle2}
                      label={a.kind === "alert" ? "Clear this alert" : "Done — take it off the chart"}
                      disabled={busy === a.id}
                    />
                  </div>
                )}
              </li>
            );
          })}

          {!(schedulingAlert ?? "").trim() && alerts.length === 0 && !adding && (
            <li>
              <PanelEmpty>
                Nothing outstanding. Add an alert, or the date of the next lab work or consultation.
              </PanelEmpty>
            </li>
          )}
        </ul>
      )}
    </Panel>
  );
}

/** The scheduling alert's own editor — it lives on the client, not in the list. */
function SchedulingAlertEditor({
  contactId, value, onClose, onSaved,
}: {
  contactId: string; value: string;
  onClose: () => void; onSaved: (v: string | null) => void;
}) {
  const toast = useToast();
  const [text, setText] = useState(value);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const next = text.trim() || null;
    try {
      await api.patch(`/contacts/${contactId}`, { scheduling_alert: next });
      toast.success(next ? "Alert saved" : "Alert cleared");
      onSaved(next);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2">
      <Label>Scheduling alert</Label>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} autoFocus
        placeholder="e.g. Needs amylase/lipase before the next IV" />
      <div className="flex gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save</Button>
        <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
      <p className="text-[10px] text-zinc-500">Empty it to take the alert off. Clearing it here clears it everywhere.</p>
    </div>
  );
}

/**
 * Add, or change, one line of this list. The same editor either way, so the
 * dialog she learns once is the one she gets back. ("And be able to change
 * edit or create them in this area.")
 */
function AlertEditor({
  contactId, existing, taken, hasSchedulingAlert, onSchedulingAlertSaved, onClose, onSaved,
}: {
  contactId: string;
  existing?: ClientAlert;
  taken: Set<string>;
  hasSchedulingAlert: boolean;
  onSchedulingAlertSaved: (v: string | null) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState<ClientAlert["kind"] | "scheduling">(existing?.kind ?? "alert");
  const [body, setBody] = useState(existing?.body ?? "");
  const [due, setDue] = useState(existing?.due_date ?? "");
  const [saving, setSaving] = useState(false);

  // One open lab date and one open consult date per client: "the next lab
  // work" is a single fact, so a kind already on the list is not offered
  // again — she edits the one that is there.
  const options: { value: string; label: string }[] = [
    { value: "alert", label: "Alert" },
    ...(existing?.kind === "followup_lab" || !taken.has("followup_lab")
      ? [{ value: "followup_lab", label: "Follow up Lab Work" }] : []),
    ...(existing?.kind === "followup_consult" || !taken.has("followup_consult")
      ? [{ value: "followup_consult", label: "Follow up Consultations" }] : []),
    ...(!existing && !hasSchedulingAlert
      ? [{ value: "scheduling", label: "Scheduling alert (shows on the front desk)" }] : []),
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (kind === "scheduling") {
        const next = body.trim() || null;
        if (!next) { toast.error("Write the alert first"); setSaving(false); return; }
        await api.patch(`/contacts/${contactId}`, { scheduling_alert: next });
        onSchedulingAlertSaved(next);
        toast.success("Alert saved");
        onSaved();
        return;
      }
      if (kind === "alert" && !body.trim()) { toast.error("Write the alert first"); setSaving(false); return; }
      if (kind !== "alert" && !due) { toast.error("Pick a date"); setSaving(false); return; }
      if (existing) {
        await api.patch(`/clinical/alerts/${existing.id}`, {
          ...(existing.kind === "alert" ? { body: body.trim() } : { due_date: due }),
        });
      } else {
        await api.post(`/clinical/${contactId}/alerts`, {
          kind,
          ...(kind === "alert" ? { body: body.trim() } : { due_date: due }),
        });
      }
      toast.success(existing ? "Saved" : "Added");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {existing ? (
        <Label>{ALERT_KIND_LABEL[existing.kind]}</Label>
      ) : (
        <div>
          <Label htmlFor="alert-kind">What is it?</Label>
          <Select id="alert-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
      )}
      {kind === "alert" || kind === "scheduling" ? (
        <Textarea
          value={body} onChange={(e) => setBody(e.target.value)} rows={2} autoFocus
          placeholder={kind === "scheduling"
            ? "e.g. Needs amylase/lipase before the next IV"
            : "e.g. Waiting on records from her cardiologist"}
        />
      ) : (
        <div>
          <Label htmlFor="alert-due">Date</Label>
          {/* A year typed as two digits is kept as typed by the date box —
              "04/04/26" became the year 0426 on a real chart. The browser
              refuses it now, and so does the server. */}
          <Input id="alert-due" type="date" min="1900-01-01" max="2200-12-31"
            value={due} onChange={(e) => setDue(e.target.value)} autoFocus />
          <p className="mt-1 text-[11px] text-zinc-500">
            {kind === "followup_lab"
              ? "The same date as Follow up Lab Work in the appointment box. Uploading in-house labs sets this on its own."
              : "The same date as Follow up Consultations in the appointment box. A completed consultation sets this on its own."}
          </p>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={save} loading={saving}>{existing ? "Save" : "Add"}</Button>
        <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── Client portal link ────────────────────────────────────────────────────
//
// [31:44] "I want them to be able … only to see what they have left, what
// they don't have left." The link is per-client and revocable; copy it into a
// text and that's the whole flow. Deliberately not a login.

export function PortalLinkPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      const d = await api.post<{ url: string }>(`/frontdesk/clients/${contactId}/portal-link`, {});
      setUrl(d.url);
      await navigator.clipboard.writeText(d.url).catch(() => {});
      toast.success("Link copied — paste it into a text to them");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't create that link");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/frontdesk/clients/${contactId}/portal-link`);
      setUrl(null);
      toast.success("Old link switched off");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't switch it off");
    } finally { setBusy(false); }
  };

  return (
    <Panel title="Their own page">
      <div className="px-4 py-3">
        <p className="text-[11px] leading-relaxed text-zinc-500">
          A private page showing only their upcoming visits and what they have left
          on a package — nothing clinical.
        </p>
        {url && (
          <p className="mt-2 truncate rounded bg-zinc-800/60 px-2 py-1 font-mono text-[10px] text-zinc-400"
             title={url}>{url}</p>
        )}
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="secondary" onClick={issue} disabled={busy}>
            {url ? "Copy again" : "Create & copy link"}
          </Button>
          {url && (
            <Button size="sm" variant="ghost" onClick={revoke} disabled={busy}
              className="text-zinc-500 hover:text-rose-400">
              Switch off
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
}

// ─── Card on file ──────────────────────────────────────────────────────────
//
// "Have to take CC at booking. I think if the CC is on file we can do the
// charging at the desk."
//
//   "can you please add a way to manually add credit card to the wallet the
//    link is difficult for some of our clients"
//
// Three ways a card gets on file, and the rule is the same for all of them:
// the digits are typed on the processor's own page, or read off the card by
// the reader. No card number ever reaches this software. All that is held
// here is the brand and the last four.
//
//   • Text them the link and they do it themselves.
//   • Tap it on the reader - nobody types anything, which is the answer for
//     a client standing at the desk who cannot follow a link.
//   • Type it in here - the same processor page, opened on the desk's own
//     screen, for a client on the phone.

interface CardOnFileRow {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  label: string;
  added_at: string;
  /** The one the desk reaches for without being asked. */
  is_default?: boolean;
}

export function CardOnFilePanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [card, setCard] = useState<CardOnFileRow | null>(null);
  // "can a client keep two different cards on file?" - hers and her
  // husband's, say, and a split bill can take part on each.
  const [cards, setCards] = useState<CardOnFileRow[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallbackShim(() => {
    api.get<{ card: CardOnFileRow | null; cards?: CardOnFileRow[]; url: string }>(`/frontdesk/clients/${contactId}/card`)
      .then((d) => { setCard(d.card); setCards(d.cards ?? (d.card ? [d.card] : [])); setUrl(d.url); })
      .catch(() => { setCard(null); setCards([]); setUrl(null); })
      .finally(() => setLoaded(true));
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Card link copied - paste it into a text to them");
  };

  /**
   * "Wallet link/ it wasent even being sent out I copied and pasted and it
   *  did not go thru to even try"
   *
   * The button only ever copied. This actually sends it, the same way her
   * forms go out, so a card can be collected without leaving the chart.
   */
  const [sending, setSending] = useState(false);
  const sendLink = async () => {
    setSending(true);
    try {
      const r = await api.post<{ channel?: string }>(`/frontdesk/clients/${contactId}/card/send`, {});
      toast.success(r?.channel === "email" ? "Card link emailed" : "Card link texted",
        "They can add it themselves, and it lands on their file.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't send that card link");
    } finally { setSending(false); }
  };

  const remove = async (cardId: string) => {
    setBusy(true);
    try {
      await api.delete(`/frontdesk/clients/${contactId}/card/${cardId}`);
      toast.success("Card removed");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that card");
    } finally { setBusy(false); }
  };

  const makeDefault = async (cardId: string) => {
    setBusy(true);
    try {
      await api.post(`/frontdesk/clients/${contactId}/card/${cardId}/default`, {});
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't change that");
    } finally { setBusy(false); }
  };

  /**
   * The reader on the desk, if there is one awake.
   *
   * Never an error here: no reader simply means no button, exactly as on the
   * checkout screen. Somebody standing at the desk should not be told about
   * a device that is asleep.
   */
  const [readers, setReaders] = useState<{ id: string; label: string | null; status: string; busy: boolean }[]>([]);
  useEffect(() => {
    let off = false;
    api.get<{ readers: typeof readers }>("/pos/terminal")
      .then((d) => { if (!off) setReaders(d.readers ?? []); })
      .catch(() => { if (!off) setReaders([]); });
    return () => { off = true; };
  }, []);
  const reader = readers.find((r) => r.status === "online" && !r.busy) ?? null;

  // The tap in progress. Null when nobody is standing at the reader.
  const [tapping, setTapping] = useState(false);
  const [tapNote, setTapNote] = useState<string | null>(null);

  const tapCard = async () => {
    if (!reader) return;
    setTapping(true);
    setTapNote("Ask them to tap, insert or swipe the card on the reader.");
    try {
      await api.post(`/pos/terminal/card/${contactId}`, { reader_id: reader.id });
    } catch (err) {
      setTapping(false);
      setTapNote(null);
      toast.error(err instanceof ApiError ? err.message : "Couldn't ask the reader for a card");
    }
  };

  const cancelTap = async () => {
    setTapping(false);
    setTapNote(null);
    await api.post(`/pos/terminal/card/${contactId}/cancel`, { reader_id: reader?.id }).catch(() => {});
  };

  // Watch the reader while they tap. Stops the moment it answers with
  // anything other than "still collecting".
  useEffect(() => {
    if (!tapping) return;
    let off = false;
    const t = setInterval(async () => {
      try {
        const d = await api.get<{ status: string; card?: string | null; error?: string }>(
          `/pos/terminal/card/${contactId}`);
        if (off) return;
        if (d.status === "collecting") return;
        setTapping(false);
        setTapNote(null);
        if (d.status === "saved") { toast.success("Card saved", d.card ?? undefined); load(); }
        else if (d.status === "failed") toast.error(d.error || "That card could not be saved");
        else toast.info("Nothing was added");
      } catch { /* a blip between polls is not a failure - keep watching */ }
    }, 1500);
    return () => { off = true; clearInterval(t); };
  }, [tapping, contactId, load, toast]);

  /**
   * Type it in on the desk's own screen.
   *
   * The very same page the client gets, so the number still only ever goes
   * into the processor's own form. `desk=1` only changes where the tab lands
   * afterwards - a "card saved, close this" rather than the client's private
   * page.
   */
  const typeItIn = () => {
    if (!url) return;
    window.open(`${url}?desk=1`, "_blank", "noopener");
    setTapNote("Type the card on the page that just opened, then come back here.");
  };

  // They have finished in the other tab. Picking the card up on focus means
  // the panel is right without anybody pressing refresh, and the note about
  // the other tab goes with it rather than sitting there afterwards.
  useEffect(() => {
    const onFocus = () => { if (!tapping) { setTapNote(null); load(); } };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load, tapping]);

  return (
    <Panel title="Card on file">
      <div className="px-4 py-3">
        {!loaded ? (
          <p className="text-[11px] text-zinc-500">Checking…</p>
        ) : card ? (
          <>
            {/* Every card they keep, the one the desk reaches for first. */}
            <ul className="space-y-1.5">
              {(cards.length > 0 ? cards : [card]).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-zinc-100">
                      {c.label}
                      {cards.length > 1 && c.is_default && (
                        <span className="ml-2 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                          Default
                        </span>
                      )}
                    </span>
                    {c.exp_month && c.exp_year && (
                      <span className="block text-[11px] text-zinc-500">
                        Expires {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {cards.length > 1 && !c.is_default && (
                      <button type="button" onClick={() => void makeDefault(c.id)} disabled={busy}
                        className="rounded px-1.5 text-[11px] text-zinc-400 hover:text-zinc-200">
                        use this one
                      </button>
                    )}
                    <button type="button" onClick={() => void remove(c.id)} disabled={busy}
                      className="rounded px-1.5 text-[11px] text-zinc-500 hover:text-rose-400">
                      remove
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
              {cards.length > 1
                ? "At checkout you pick which one to charge - or split a bill across both."
                : "Charge it at checkout with \u201cCharge the card on file\u201d. Add another and they can keep both."}
            </p>
            {tapNote && (
              <p className="mt-2 rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 px-2.5 py-2 text-[11px] text-zinc-200">
                {tapNote}
              </p>
            )}
            {/* Replacing an expired card at the desk is the same three ways
                in. The new one takes over and the old token is detached.
                 *
                 * "I find it confusing with the links … I was adding his card
                 *  to his account? After he gave me a code"
                 *
                 * Every button here SAVES a card and takes no money, but
                 * "Type another in" read like a way to charge one, so she
                 * pressed it with a client waiting to pay. The row says what
                 * it is before she reaches the buttons, and where to go to
                 * actually take the money. */}
            <p className="mt-3 text-[11px] font-semibold text-zinc-300">Save another card on file</p>
            <p className="text-[11px] text-zinc-500">No money is taken. To charge them now, use Check out.</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {tapping ? (
                <Button size="sm" variant="ghost" onClick={() => void cancelTap()} className="text-zinc-400">
                  Cancel
                </Button>
              ) : (
                <>
                  {reader && (
                    <Button size="sm" variant="secondary" onClick={() => void tapCard()}>
                      On the reader
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={typeItIn} disabled={!url} className="text-zinc-300">
                    Type it in
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void sendLink()} loading={sending} disabled={!url}
                    className="text-zinc-400">
                    Text them a link
                  </Button>
                  {/* "Copy the link - is that the same as text them the
                      link? And if yes we don't need that option" - it is the
                      same link, and it is the only way to send it to somebody
                      with no mobile number on file. So it says what it is for
                      rather than disappearing. */}
                  <Button size="sm" variant="ghost" onClick={copy} disabled={!url} className="text-zinc-400">
                    Copy it for an email
                  </Button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-[11px] leading-relaxed text-zinc-500">
              No card yet. However it goes on, the number is typed on our payment provider&rsquo;s own
              secure page or read by the reader - we never see it.
            </p>
            {tapNote && (
              <p className="mt-2 rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 px-2.5 py-2 text-[11px] text-zinc-200">
                {tapNote}
              </p>
            )}
            <p className="mt-3 text-[11px] font-semibold text-zinc-300">Save a card on file</p>
            <p className="text-[11px] text-zinc-500">No money is taken. To charge them now, use Check out.</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {tapping ? (
                <Button size="sm" variant="ghost" onClick={() => void cancelTap()} className="text-zinc-400">
                  Cancel
                </Button>
              ) : (
                <>
                  {reader && (
                    <Button size="sm" variant="secondary" onClick={() => void tapCard()}>
                      On the reader
                    </Button>
                  )}
                  <Button size="sm" variant={reader ? "ghost" : "secondary"} onClick={typeItIn} disabled={!url}
                    className={reader ? "text-zinc-300" : undefined}>
                    Type it in
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void sendLink()} loading={sending} disabled={!url}
                    className="text-zinc-400">
                    Text them a link
                  </Button>
                  {/* "Copy the link - is that the same as text them the
                      link? And if yes we don't need that option" - it is the
                      same link, and it is the only way to send it to somebody
                      with no mobile number on file. So it says what it is for
                      rather than disappearing. */}
                  <Button size="sm" variant="ghost" onClick={copy} disabled={!url} className="text-zinc-400">
                    Copy it for an email
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

// ─── Clinical sections ─────────────────────────────────────────────────────
//
// Her second-biggest complaint on the call [06:42–09:08]:
//
//   "I'm not able to change these little pocket areas. So this is for
//    medications, which is great. But like here, it only says allergies …
//    We put our lab scripts in here. We need an area if I need to be able to
//    curate the software that I'm able to upload lab scripts."
//
//   "it gets really confusing because they have lab scripts in their hand and
//    they have to go to the allergies … and in the history. You have to go to
//    clinical in the allergies to get the lab script."
//
// So: the boxes are hers to name, and each holds dated, signed entries with a
// file attached — a lab script goes in "Lab scripts", where a new nurse will
// actually look for it.

interface ClinicalEntry {
  id: string; section_id: string; body: string;
  file_url: string | null; file_name: string | null;
  author_name: string | null; author_credential: string | null;
  created_at: string;
}
interface ClinicalSection {
  id: string; name: string; is_alert: boolean; position: number;
  entries: ClinicalEntry[];
}

export function ClinicalPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [sections, setSections] = useState<ClinicalSection[] | null>(null);
  const [error, setError] = useState(false);
  const [adding, setAdding] = useState<ClinicalSection | null>(null);
  const [managing, setManaging] = useState(false);

  const load = useCallbackShim(() => {
    setError(false);
    api.get<{ sections: ClinicalSection[] }>(`/clinical/${contactId}`)
      .then((d) => setSections(d.sections))
      .catch(() => { setSections(null); setError(true); });
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <PanelErrorRow message="Couldn't load the clinical record" onRetry={load} />;
  if (!sections) return <Skeleton className="h-40" />;

  return (
    <>
      <Panel
        title="Clinical"
        action={
          <button onClick={() => setManaging(true)}
            className="text-[11px] text-zinc-500 hover:text-zinc-300">Edit sections</button>
        }
      >
        <div className="divide-y divide-zinc-800/40">
          {sections.map((s) => (
            <div key={s.id} className="px-4 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <h4 className={cn("flex items-center gap-1.5 text-xs font-semibold",
                  s.is_alert ? "text-rose-300" : "text-zinc-300")}>
                  {s.is_alert && <AlertTriangle className="h-3 w-3" />}
                  {s.name}
                  {s.entries.length > 0 && (
                    <span className="font-normal text-zinc-600">({s.entries.length})</span>
                  )}
                </h4>
                <button onClick={() => setAdding(s)}
                  className="text-zinc-500 hover:text-zinc-200" title={`Add to ${s.name}`}>
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              {s.entries.length === 0 ? (
                <p className={cn("mt-1 text-[11px]", s.is_alert ? "text-zinc-500" : "text-zinc-600")}>
                  {s.is_alert ? "No known allergies recorded." : "Nothing yet."}
                </p>
              ) : (
                <ul className="mt-1.5 space-y-1.5">
                  {s.entries.map((e) => (
                    <li key={e.id} className={cn("rounded-lg px-2 py-1.5",
                      s.is_alert ? "bg-rose-500/10" : "bg-zinc-800/40")}>
                      {e.body && (
                        <p className={cn("whitespace-pre-wrap text-xs",
                          s.is_alert ? "text-rose-100" : "text-zinc-200")}>{e.body}</p>
                      )}
                      {e.file_url && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await api.get<{ url: string }>(`/clinical/entries/${e.id}/view-url`);
                              window.open(r.url, "_blank", "noopener");
                            } catch { toast.error("Couldn't open that file"); }
                          }}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-indigo-300 hover:underline">
                          <FileIcon className="h-3 w-3" /> {e.file_name ?? "Attachment"}
                        </button>
                      )}
                      <p className="mt-0.5 text-[10px] text-zinc-500">
                        {[e.author_name, e.author_credential].filter(Boolean).join(" ")}
                        {e.author_name ? " · " : ""}
                        {formatDate(e.created_at, { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Panel>

      {adding && (
        <AddClinicalEntry contactId={contactId} section={adding}
          onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />
      )}
      {managing && (
        <ManageSections sections={sections} onClose={() => setManaging(false)}
          onChanged={() => load()} />
      )}
    </>
  );
}

/** Tiny local useCallback so this block doesn't depend on import order. */
function useCallbackShim(fn: () => void, deps: unknown[]) {
  const ref = useRef(fn);
  ref.current = fn;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemoShim(() => () => ref.current(), deps);
}
function useMemoShim<T>(make: () => T, deps: unknown[]): T {
  const box = useRef<{ deps: unknown[]; value: T } | null>(null);
  if (!box.current || box.current.deps.some((d, i) => d !== deps[i])) {
    box.current = { deps, value: make() };
  }
  return box.current.value;
}

function AddClinicalEntry({
  contactId, section, onClose, onSaved,
}: { contactId: string; section: ClinicalSection; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [body, setBody] = useState("");
  const [file, setFile] = useState<{ url: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f: File) => {
    setBusy(true);
    try {
      // Clinical attachments are private, same bucket as lab documents.
      const up = await uploadFile(f, {
        path: `clinical/${contactId}/${makeUploadFilename(f.name)}`,
        bucket: "private",
      });
      setFile({ url: up.key, name: f.name });
    } catch {
      toast.error("That file didn't upload");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/clinical/entries", {
        contact_id: contactId, section_id: section.id, body,
        file_url: file?.url ?? null, file_name: file?.name ?? null,
      });
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Add to ${section.name}`}>
      <div className="space-y-3">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} autoFocus
          placeholder={section.is_alert
            ? "e.g. Penicillin — hives"
            : `Anything worth recording under ${section.name.toLowerCase()}`} />
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Attach a file (optional)</label>
          <input type="file" onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])}
            className="block w-full text-xs text-zinc-400 file:mr-2 file:rounded-md file:border-0 file:bg-zinc-800 file:px-2 file:py-1 file:text-zinc-200" />
          {file && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-400">
              <FileIcon className="h-3 w-3" /> {file.name}
            </p>
          )}
          <p className="mt-1 text-[10px] text-zinc-500">
            Lab scripts and results belong here — they'll be signed with your name and dated.
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy || (!body.trim() && !file)}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}

function ManageSections({
  sections, onClose, onChanged,
}: { sections: ClinicalSection[]; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/clinical/sections", { name: name.trim(), position: sections.length });
      setName(""); onChanged();
      toast.success("Section added");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that");
    } finally { setBusy(false); }
  };

  const rename = async (id: string, next: string) => {
    try { await api.patch(`/clinical/sections/${id}`, { name: next }); onChanged(); }
    catch { toast.error("Couldn't rename that"); }
  };
  const archive = async (id: string) => {
    try { await api.patch(`/clinical/sections/${id}`, { archived: true }); onChanged(); }
    catch { toast.error("Couldn't remove that"); }
  };

  return (
    <Modal open onClose={onClose} title="Clinical sections">
      <p className="mb-3 text-xs text-zinc-500">
        These are the boxes on every client's chart. Name them however your team thinks —
        a lab script should live under "Lab scripts", not under allergies.
      </p>
      <ul className="space-y-1.5">
        {sections.map((s) => (
          <li key={s.id} className="flex items-center gap-2">
            <Input defaultValue={s.name} className="h-8 text-sm"
              onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && rename(s.id, e.target.value.trim())} />
            {s.is_alert && <Badge color="rose" size="xs">Alert</Badge>}
            <button onClick={() => archive(s.id)} title="Remove"
              className="shrink-0 text-zinc-600 hover:text-rose-400">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2 border-t border-zinc-800 pt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Add a section — e.g. Insurance cards" className="h-8 text-sm" />
        <Button size="sm" onClick={add} disabled={busy || !name.trim()}>Add</Button>
      </div>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── One clinical file ─────────────────────────────────────────────────────
//
// Her complaint was never really about labels — it was about hunting [09:08]:
//
//   "we have to tell the new staff and people that are coming in, you have to
//    go to history to get the lab results. You have to go to clinical in the
//    allergies to get the lab script — the whole thing kind of is hard to
//    grasp."
//
// The first version of this build renamed the boxes and kept the hunting:
// entries in one panel, uploads in another, signed charts in a third, forms in
// a fourth. This is all four on one dated stream, filterable, with allergies
// pinned above it. A new nurse opens the file and reads down.

interface FileItem {
  id: string; kind: string; kind_label: string;
  section: string | null; title: string; body: string;
  file: { name: string; via: string } | null;
  author: string | null; status: string | null;
  is_alert: boolean; at: string;
}
interface FileAlert { id: string; section: string; body: string }

const KIND_TONE: Record<string, string> = {
  entry: "text-sky-300", document: "text-amber-300",
  chart: "text-emerald-300", form: "text-violet-300",
};

export function ClientFilePanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [items, setItems] = useState<FileItem[] | null>(null);
  const [alerts, setAlerts] = useState<FileAlert[]>([]);
  const [sections, setSections] = useState<{ id: string; name: string; is_alert: boolean }[]>([]);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState(false);

  const load = useCallbackShim(() => {
    setFailed(false);
    api.get<{ items: FileItem[]; alerts: FileAlert[]; sections: typeof sections }>(`/clinical/${contactId}/file`)
      .then((d) => { setItems(d.items); setAlerts(d.alerts); setSections(d.sections); })
      .catch(() => { setItems(null); setFailed(true); });
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  if (failed) return <PanelErrorRow message="Couldn't load this client's file" onRetry={load} />;
  if (!items) return <Skeleton className="h-64" />;

  // Only offer filters for the kinds this client actually has.
  const kinds = Array.from(new Set(items.map((i) => i.kind)));
  const shown = filter === "all" ? items : items.filter((i) => i.kind === filter);
  const KIND_NAME: Record<string, string> = {
    entry: "Clinical", document: "Documents", chart: "Visit notes", form: "Forms",
  };

  const openFile = async (it: FileItem) => {
    try {
      const path = it.file?.via === "document"
        ? `/documents/${it.id}/view-url`
        : `/clinical/entries/${it.id}/view-url`;
      const r = await api.get<{ url: string }>(path);
      window.open(r.url, "_blank", "noopener");
    } catch { toast.error("Couldn't open that file"); }
  };

  return (
    <>
      <Panel
        title="Clinical file"
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => setManaging(true)}
              className="text-[11px] text-zinc-500 hover:text-zinc-300">Sections</button>
            <button onClick={() => setAdding(true)}
              className="flex items-center gap-1 text-[11px] text-indigo-300 hover:text-indigo-200">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        }
      >
        {/* Allergies sit above everything, always. */}
        {alerts.length > 0 && (
          <div className="border-b border-rose-900/40 bg-rose-500/10 px-4 py-2">
            {alerts.map((a) => (
              <p key={a.id} className="flex items-start gap-1.5 text-xs text-rose-200">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                <span><span className="font-semibold">{a.section}:</span> {a.body}</span>
              </p>
            ))}
          </div>
        )}

        {kinds.length > 1 && (
          <div className="flex flex-wrap gap-1 border-b border-zinc-800/60 px-3 py-2">
            {["all", ...kinds].map((k) => (
              <button key={k} onClick={() => setFilter(k)}
                className={cn("rounded-md px-2 py-0.5 text-[11px]",
                  filter === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300")}>
                {k === "all" ? "Everything" : KIND_NAME[k] ?? k}
              </button>
            ))}
          </div>
        )}

        {shown.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-zinc-500">
            Nothing on file yet. Lab scripts, results, letters and visit notes all live here.
          </p>
        ) : (
          <ul className="max-h-[28rem] divide-y divide-zinc-800/40 overflow-y-auto">
            {shown.map((it) => (
              <li key={`${it.kind}-${it.id}`} className="px-4 py-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={cn("text-[10px] font-semibold uppercase tracking-wider",
                    KIND_TONE[it.kind] ?? "text-zinc-400")}>
                    {it.kind_label}
                  </span>
                  <span className="shrink-0 text-[10px] text-zinc-500">
                    {formatDate(it.at, { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
                {it.kind !== "entry" && (
                  <p className="mt-0.5 text-xs font-medium text-zinc-200">{it.title}</p>
                )}
                {it.body && (
                  <p className="mt-0.5 whitespace-pre-wrap text-xs text-zinc-300">{it.body}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {it.file && (
                    <button onClick={() => openFile(it)}
                      className="inline-flex items-center gap-1 text-[11px] text-indigo-300 hover:underline">
                      <FileIcon className="h-3 w-3" /> {it.file.name}
                    </button>
                  )}
                  {it.status && (
                    <Badge size="xs"
                      color={it.status.includes("Waiting") || it.status === "Needs review" ? "amber"
                        : it.status === "Signed" || it.status === "Completed" ? "emerald" : "zinc"}>
                      {it.status}
                    </Badge>
                  )}
                  {it.author && <span className="text-[10px] text-zinc-500">{it.author}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {adding && (
        <AddToFile contactId={contactId} sections={sections}
          onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
      )}
      {managing && (
        <ManageSections sections={sections.map((s) => ({ ...s, position: 0, entries: [] }))}
          onClose={() => setManaging(false)} onChanged={load} />
      )}
    </>
  );
}

/** Add anything to the file — pick the box, write it, attach the PDF. */
function AddToFile({
  contactId, sections, onClose, onSaved,
}: {
  contactId: string;
  sections: { id: string; name: string; is_alert: boolean }[];
  onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? "");
  const [body, setBody] = useState("");
  const [file, setFile] = useState<{ key: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f: File) => {
    setBusy(true);
    try {
      const up = await uploadFile(f, {
        path: `clinical/${contactId}/${makeUploadFilename(f.name)}`,
        bucket: "private",
      });
      setFile({ key: up.key, name: f.name });
    } catch { toast.error("That file didn't upload"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/clinical/entries", {
        contact_id: contactId, section_id: sectionId, body,
        file_url: file?.key ?? null, file_name: file?.name ?? null,
      });
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setBusy(false); }
  };

  const section = sections.find((s) => s.id === sectionId);

  return (
    <Modal open onClose={onClose} title="Add to the file">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs text-zinc-400">What is it?</label>
          <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} autoFocus
          placeholder={section?.is_alert ? "e.g. Penicillin — hives" : "Anything worth recording"} />
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Attach a file (optional)</label>
          <input type="file" onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])}
            className="block w-full text-xs text-zinc-400 file:mr-2 file:rounded-md file:border-0 file:bg-zinc-800 file:px-2 file:py-1 file:text-zinc-200" />
          {file && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-400">
              <FileIcon className="h-3 w-3" /> {file.name}
            </p>
          )}
        </div>
        <p className="text-[10px] text-zinc-500">
          It'll be dated and signed with your name automatically.
        </p>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy || !sectionId || (!body.trim() && !file)}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
